<template>
	<div class="user-card-page p-2">
		<div class="user-card-header">
			<div>
				<b-button size="sm" variant="link" class="p-0 mb-2" @click="goBack">
					<i class="fas fa-arrow-left mr-1"></i>
					{{ isProfile ? 'Назад' : 'К учётным записям' }}
				</b-button>
				<h4 class="mb-1">{{ pageTitle }}</h4>
				<div v-if="isEdit" class="text-muted small">
					#{{ userId }}
					<span class="mx-1">·</span>
					{{ active ? 'Учётная запись активна' : 'Учётная запись отключена' }}
					<span class="mx-1">·</span>
					{{ cabinetAccessEnabled ? 'ЛК включен' : 'ЛК выключен' }}
				</div>
			</div>
			<div class="user-card-header__actions">
				<b-button
					v-if="isEdit && !isProfile && employeeCardId"
					size="sm"
					variant="outline-primary"
					:to="`/employees/${employeeCardId}`"
				>
					<i class="fas fa-address-card mr-1"></i>
					Карточка сотрудника
				</b-button>
				<b-button size="sm" variant="outline-secondary" @click="isEditingForm ? cancelEdit() : goBack()">
					Отмена
				</b-button>
				<b-button
					v-if="isEdit && !isProfile && canManageAccess && !isCurrentUser"
					v-b-tooltip.hover
					size="sm"
					:variant="active ? 'outline-warning' : 'outline-success'"
					:title="active
						? 'Отключит личный кабинет и завершит активные сессии'
						: 'Включит учётную запись после проверки статуса сотрудника'"
					:disabled="saving || statusImpactLoading || statusSubmitting"
					@click="openStatusModal"
				>
					<i :class="active ? 'fas fa-user-slash mr-1' : 'fas fa-user-check mr-1'"></i>
					{{ active ? 'Отключить' : 'Включить' }}
				</b-button>
				<b-button
					v-if="isEdit && !isEditingForm && canEditCurrentCard"
					size="sm"
					variant="outline-primary"
					@click="startEdit"
				>
					<i class="fas fa-pen mr-1"></i>
					Редактировать
				</b-button>
				<b-button
					v-if="isEditingForm"
					size="sm"
					variant="success"
					:disabled="isSubmitDisabled"
					@click="submit"
				>
					<i class="fas fa-save mr-1"></i>
					Сохранить
				</b-button>
			</div>
		</div>

		<b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = null">
			{{ errorMessage }}
		</b-alert>

		<div v-if="loading" class="text-center text-muted py-5">
			<b-spinner small class="mr-2"></b-spinner>
			Загрузка учётной записи
		</div>

		<div v-else class="user-card-layout">
			<div class="user-card-main">

				<template v-if="isEdit && !isEditingForm">
					<section class="user-card-section user-profile-overview">
						<div class="user-profile-overview__avatar user-avatar-preview">
							<img v-if="avatarUrl" :src="avatarUrl" :alt="fullName || 'Аватарка'" class="user-avatar-preview__img">
							<div v-else class="user-avatar-preview__fallback">
								{{ avatarInitials }}
							</div>
						</div>
						<div class="user-profile-overview__body">
							<div class="user-card-section__header mb-1">
								<h6>{{ cardDisplayName || 'Сотрудник' }}</h6>
								<b-badge :variant="active ? 'success' : 'secondary'">
									{{ active ? 'Действующий' : 'Отключён' }}
								</b-badge>
							</div>
							<div class="user-profile-overview__subtitle">
								{{ position || 'Должность не указана' }}
								<span v-if="employmentOrganizationName">
									· {{ employmentOrganizationName }}
								</span>
							</div>
							<div class="user-profile-facts">
								<div v-if="hasEmployeeField('avatar_source') || hasEmployeeField('has_avatar') || hasEmployeeField('avatar_url')" class="user-profile-fact">
									<span>Источник аватарки</span>
									<strong>{{ avatarSourceLabel }}</strong>
								</div>
								<div v-if="hasEmployeeField('phone')" class="user-profile-fact">
									<span>Телефон</span>
									<strong>{{ formatPhone(phone, 'Не указан') }}</strong>
								</div>
								<div v-if="hasEmployeeField('telegram_username') || hasEmployeeField('telegram_url') || hasEmployeeField('chat_id')" class="user-profile-fact">
									<span>Telegram</span>
									<a
										v-if="telegramLink"
										:href="telegramLink"
										target="_blank"
										rel="noopener"
										class="user-profile-link"
									>
										{{ telegramUsername ? `@${telegramUsername}` : 'Telegram' }}
									</a>
									<strong v-else>{{ telegramChatId || 'Не указан' }}</strong>
								</div>
								<div v-if="hasEmployeeField('gmail')" class="user-profile-fact">
									<span>Gmail</span>
									<strong>{{ gmail || 'Не указан' }}</strong>
								</div>
								<div v-if="hasEmployeeField('departments')" class="user-profile-fact user-profile-fact--wide">
									<span>Отделы</span>
									<strong>{{ departmentsText || 'Нет отделов' }}</strong>
								</div>
								<div v-if="!isProfile && canReadRawUsers" class="user-profile-fact">
									<span>Личный кабинет</span>
									<strong>{{ cabinetAccessEnabled ? 'Включен' : 'Выключен' }}</strong>
								</div>
								<div v-if="!isProfile && canReadRawUsers" class="user-profile-fact">
									<span>Логин</span>
									<strong>{{ login || 'Не указан' }}</strong>
								</div>
								<div v-if="!isProfile && canReadRawUsers" class="user-profile-fact user-profile-fact--wide">
									<span>Роли</span>
									<strong>{{ rolesText || 'Нет ролей' }}</strong>
								</div>
							</div>
						</div>
					</section>

					<AccountSecurity v-if="isEdit && userId > 0 && (isProfile || ($store.getters.currentUser.roles || []).includes('Admin'))" :user-id="userId" :admin="($store.getters.currentUser.roles || []).includes('Admin')"/>

					<section v-if="hasVisibleContacts" class="user-card-section">
						<div class="user-card-section__header">
							<h6>Контактные данные</h6>
						</div>
						<div class="employee-contact-list">
							<div v-for="item in visiblePhoneContacts" :key="item.key" class="employee-contact-row">
								<i class="fas fa-phone-alt employee-contact-row__icon"></i>
								<div>
									<a :href="phoneHref(item.value)">{{ formatPhone(item.value) }}</a>
									<div class="small text-muted">{{ item.label }}</div>
								</div>
							</div>
							<div v-for="item in visibleEmailContacts" :key="item.key" class="employee-contact-row">
								<i class="fas fa-envelope employee-contact-row__icon"></i>
								<div>
									<a :href="`mailto:${item.value}`">{{ item.value }}</a>
									<div class="small text-muted">{{ item.label }}</div>
								</div>
							</div>
							<div
								v-for="(address, index) in contactAddresses"
								:key="address.id || `address-${index}`"
								class="employee-contact-row"
							>
								<i class="fas fa-map-marker-alt employee-contact-row__icon"></i>
								<div>
									<div>{{ addressDisplay(address) }}</div>
									<div v-if="address.label" class="small text-muted">{{ address.label }}</div>
								</div>
							</div>
						</div>
					</section>

					<section v-if="hasVisibleDetails" class="user-card-section">
						<div class="user-card-section__header">
							<h6>Дополнительная информация</h6>
						</div>
						<div class="employee-details-grid">
							<div v-if="birthDate" class="employee-detail">
								<span>Дата рождения</span>
								<strong>{{ formatBirthDate(birthDate) }}</strong>
							</div>
							<div v-if="officeRoom" class="employee-detail">
								<span>Кабинет</span>
								<strong>{{ officeRoom }}</strong>
							</div>
							<div v-if="employmentOrganizationName" class="employee-detail">
								<span>Организация-работодатель</span>
								<strong>{{ employmentOrganizationName }}</strong>
							</div>
							<div v-if="contactLabels.length" class="employee-detail employee-detail--wide">
								<span>Ярлыки</span>
								<div>
									<b-badge
										v-for="label in contactLabels"
										:key="label"
										variant="light"
										class="mr-1 mb-1"
									>{{ label }}</b-badge>
								</div>
							</div>
							<div v-if="notes" class="employee-detail employee-detail--wide">
								<span>Дополнительная информация</span>
								<div class="employee-detail__multiline">{{ notes }}</div>
							</div>
							<div
								v-for="(field, index) in customFields"
								:key="`${field.label}-${index}`"
								class="employee-detail"
							>
								<span>{{ field.label || 'Дополнительное поле' }}</span>
								<strong>{{ field.value || '—' }}</strong>
							</div>
							<div
								v-for="profile in externalProfiles.filter(item => item.fields && item.fields.length)"
								:key="profile.id || profile.source_system"
								class="employee-detail employee-detail--wide employee-external-profile"
							>
								<span>Собственные поля · {{ sourceLabel(profile.source_system) }}</span>
								<div class="employee-external-profile__fields">
									<div v-for="(field, index) in profile.fields" :key="`${field.label}-${index}`">
										<small>{{ field.label }}</small>
										<strong>{{ field.value }}</strong>
									</div>
								</div>
							</div>
							<div v-if="externalSources.length" class="employee-detail employee-detail--wide">
								<span>Источники</span>
								<div>
									<b-badge
										v-for="source in externalSources"
										:key="source"
										variant="secondary"
										class="mr-1"
									>{{ sourceLabel(source) }}</b-badge>
								</div>
							</div>
						</div>
					</section>
				</template>

				<template v-else>
					<section class="user-card-section user-avatar-section">
						<div class="user-avatar-preview">
							<img v-if="avatarUrl" :src="avatarUrl" :alt="fullName || 'Аватарка'" class="user-avatar-preview__img">
							<div v-else class="user-avatar-preview__fallback">
								{{ avatarInitials }}
							</div>
						</div>
						<div class="user-avatar-controls">
							<div class="user-card-section__header mb-1">
								<h6>Профиль</h6>
								<b-badge :variant="avatarSource === 'telegram' ? 'info' : 'secondary'">
									{{ avatarSourceLabel }}
								</b-badge>
							</div>
							<b-form-group label="Источник аватарки" label-for="avatarSource">
								<b-form-select
									id="avatarSource"
									v-model="avatarSource"
									:options="avatarSourceOptions"
									:disabled="!canEditAvatarSource"
								></b-form-select>
							</b-form-group>
							<div class="user-avatar-actions">
								<input
									ref="avatarInput"
									type="file"
									accept="image/jpeg,image/png,image/webp"
									class="d-none"
									@change="onAvatarFileSelected"
								>
								<b-button size="sm" variant="outline-primary" :disabled="!canEditAvatar" @click="chooseAvatarFile">
									<i class="fas fa-image mr-1"></i>
									Выбрать файл
								</b-button>
								<b-button size="sm" variant="outline-secondary" :disabled="!canEditAvatar || !avatarUrl" @click="deleteAvatar">
									<i class="fas fa-times mr-1"></i>
									Убрать
								</b-button>
							</div>
							<small class="text-muted d-block mt-2">
								Синхронизация с Telegram обновится при следующем касании бота.
							</small>
						</div>
					</section>

				<section class="user-card-section">
					<div v-if="isCreate" class="account-employee-link">
						<div class="user-card-section__header">
							<div>
								<h6>Карточка сотрудника</h6>
								<div class="small text-muted mt-1">
									Сначала найдите действующего сотрудника без учётной записи.
								</div>
							</div>
							<div>
								<input
									ref="accountGoogleContactInput"
									type="file"
									accept=".csv,text/csv"
									class="d-none"
									@change="onAccountGoogleContactFile"
								>
								<b-button
									size="sm"
									variant="outline-primary"
									:disabled="accountGooglePreviewLoading"
									@click="$refs.accountGoogleContactInput.click()"
								>
									<b-spinner v-if="accountGooglePreviewLoading" small class="mr-1"></b-spinner>
									<i v-else class="fas fa-file-import mr-1"></i>
									Взять из Google CSV
								</b-button>
							</div>
						</div>
						<b-form-radio-group
							v-model="accountEmployeeMode"
							:options="[
								{value: 'existing', text: 'Выбрать существующего'},
								{value: 'new', text: 'Создать нового сотрудника'}
							]"
							buttons
							button-variant="outline-primary"
							size="sm"
							class="mb-3"
						></b-form-radio-group>
						<template v-if="accountEmployeeMode === 'existing'">
							<v-select
								v-model="selectedEmployeeCandidate"
								:options="accountCandidateOptions"
								label="label"
								:clearable="true"
								:filterable="true"
								:filter-by="accountCandidateFilter"
								:loading="accountCandidatesLoading"
								placeholder="Фамилия, имя, телефон или табельный номер"
								@input="onEmployeeCandidateSelected"
							>
								<template v-slot:option="option">
									<div>
										<strong>{{ option.label }}</strong>
										<small class="d-block text-muted">
											#{{ option.employee_id }}
											<span v-if="option.position"> · {{ option.position }}</span>
											<span v-if="option.department_name"> · {{ option.department_name }}</span>
											<span> · {{ accountEmployeeStatusLabel(option.lifecycle_status) }}</span>
										</small>
									</div>
								</template>
							</v-select>
							<b-alert
								v-if="selectedEmployeeNeedsConfirmation"
								show
								variant="warning"
								class="mt-3 mb-0"
							>
								<strong>Сотрудник пока не проверен.</strong>
								<p class="mb-2 mt-1">
									Создание учётной записи подтвердит личность сотрудника и переведёт его в действующие текущей датой.
								</p>
								<b-form-checkbox v-model="confirmUnverifiedEmployee">
									Подтверждаю, что это тот сотрудник. Перевести в действующие.
								</b-form-checkbox>
							</b-alert>
							<b-alert
								v-if="!accountCandidatesLoading && !accountCandidateOptions.length"
								show
								variant="secondary"
								class="py-2 mt-2 mb-0"
							>
								Сотрудников без учётной записи не найдено.
							</b-alert>
						</template>
						<b-alert v-else show variant="warning" class="py-2 mb-0">
							Будет создана новая действующая карточка сотрудника и связанная
							с ней учётная запись. Перед сохранением проверьте, что человека
						 действительно нет в реестре.
						</b-alert>
						<b-alert
							v-if="accountEmployeeDuplicates.length"
							show
							variant="warning"
							class="mt-3 mb-0"
						>
							<strong>Найдены похожие карточки:</strong>
							<ul class="mb-2 mt-2">
								<li
									v-for="employee in accountEmployeeDuplicates"
									:key="employee.employee_id"
								>
									<router-link
										:to="`/employees/${employee.employee_id}`"
										target="_blank"
									>
										{{ employee.full_name }}
									</router-link>
									<span v-if="employee.birth_date">
										· {{ formatDate(employee.birth_date) }}
									</span>
									<ul
										v-if="employee.match_reasons && employee.match_reasons.length"
										class="mb-1 mt-1"
									>
										<li
											v-for="reason in employee.match_reasons"
											:key="reason"
										>
											{{ reason }}
										</li>
									</ul>
								</li>
							</ul>
							<b-form-checkbox v-model="accountEmployeeDuplicateConfirmed">
								Это другой человек. Создать новую карточку и учётную запись.
							</b-form-checkbox>
						</b-alert>
					</div>
					<div class="user-card-section__header">
						<h6>Основные данные</h6>
					</div>
					<div class="user-card-grid">
						<b-form-group label="Фамилия" label-for="lastName">
							<b-form-input v-model.trim="lastName" id="lastName" :disabled="isProfile || linkedEmployeeFieldsReadOnly"></b-form-input>
						</b-form-group>
						<b-form-group label="Имя" label-for="firstName">
							<b-form-input v-model.trim="firstName" id="firstName" :disabled="isProfile || linkedEmployeeFieldsReadOnly"></b-form-input>
						</b-form-group>
						<b-form-group label="Отчество" label-for="middleName">
							<b-form-input
								v-model.trim="middleName"
								id="middleName"
								:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"
							></b-form-input>
						</b-form-group>
						<b-form-group label="Должность" label-for="position">
							<b-form-input
								v-model.trim="position"
								id="position"
								:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('position'))"
							></b-form-input>
						</b-form-group>
						<b-form-group label="Организация-работодатель" label-for="employmentOrganization">
							<b-input-group>
								<b-form-select
									id="employmentOrganization"
									v-model="employmentOrganizationId"
									:options="employmentOrganizationOptions"
									:disabled="isProfile && !canEditSelfField('details')"
								></b-form-select>
								<b-input-group-append v-if="!isProfile && canSave">
									<b-button
										variant="outline-secondary"
										title="Добавить организацию"
										@click="openOrganizationModal"
									>
										<i class="fas fa-plus"></i>
									</b-button>
								</b-input-group-append>
							</b-input-group>
						</b-form-group>
						<b-form-group label="Дата рождения" label-for="birthDate">
							<b-form-input
								id="birthDate"
								v-model="birthDate"
								type="date"
								:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"
							></b-form-input>
						</b-form-group>
						<b-form-group label="Кабинет" label-for="officeRoom">
							<b-form-input
								id="officeRoom"
								v-model.trim="officeRoom"
								:disabled="isProfile && !canEditSelfField('details')"
							></b-form-input>
						</b-form-group>
						<b-form-group label="Основной телефон · Telegram" label-for="phone">
							<phone-input
								v-model="phone"
								id="phone"
								:disabled="isProfile && !canEditSelfField('phone')"
							></phone-input>
						</b-form-group>
						<b-form-group label="Telegram ID" label-for="telegramChatId">
							<b-form-input
								v-model.trim="telegramChatId"
								id="telegramChatId"
								:disabled="isProfile"
							></b-form-input>
						</b-form-group>
						<b-form-group label="Системный Gmail" label-for="Gmail">
							<b-form-input
								v-model.trim="gmail"
								id="Gmail"
								:state="gmailState"
								:disabled="isProfile && !canEditSelfField('gmail')"
								@blur="normalizeGmailField"
							></b-form-input>
							<b-form-invalid-feedback :state="gmailState">
								{{ gmailErrorMessage }}
							</b-form-invalid-feedback>
						</b-form-group>
					</div>
					<small class="text-muted d-block mb-3">
						Основной телефон используется для Telegram, системный Gmail — для интеграций Аргуса. Остальные контакты добавляются ниже.
					</small>
					<div v-if="!isProfile" class="user-card-switches">
						<b-form-checkbox v-model="isBot" name="isBot" switch>
							Является ботом
						</b-form-checkbox>
					</div>
					<b-form-group label="Отделы" class="mb-0">
						<b-form-checkbox-group
							v-model="selectedDepartments"
							:options="departmentOptions"
							:disabled="isProfile"
							stacked
						></b-form-checkbox-group>
					</b-form-group>
				</section>

				<section class="user-card-section">
					<div class="user-card-section__header">
						<div>
							<h6>Дополнительные контакты</h6>
							<div class="small text-muted mt-1">
								Все телефоны и адреса почты показываются в одной карточке с подписями из Google Контактов.
							</div>
						</div>
						<b-button
							v-if="!linkedEmployeeFieldsReadOnly && (!isProfile || canEditAnySelfContact)"
							size="sm"
							variant="outline-primary"
							@click="addContactMethod"
						>
							<i class="fas fa-plus mr-1"></i>
							Добавить
						</b-button>
					</div>
					<div v-if="contactMethods.length" class="employee-repeatable-list">
						<div
							v-for="(method, index) in contactMethods"
							:key="method.local_id || method.id || index"
							class="employee-repeatable-row employee-repeatable-row--contact"
						>
							<b-form-select
								v-model="method.contact_type"
								:options="contactTypeOptions"
								:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canChangeSelfContactType)"
							></b-form-select>
							<b-form-input
								v-model.trim="method.label"
								placeholder="Подпись: мобильный, рабочий…"
								:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfContactMethod(method))"
							></b-form-input>
							<phone-input
								v-if="method.contact_type === 'phone'"
								v-model="method.value"
								mode="contact"
								placeholder="+375291234567"
								:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfContactMethod(method))"
							></phone-input>
							<b-form-input
								v-else
								v-model.trim="method.value"
								:placeholder="method.contact_type === 'email' ? 'email@example.com' : '+375…'"
								:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfContactMethod(method))"
							></b-form-input>
							<b-button
								v-if="!linkedEmployeeFieldsReadOnly && (!isProfile || canEditSelfContactMethod(method))"
								variant="outline-danger"
								title="Удалить контакт"
								@click="removeContactMethod(index)"
							>
								<i class="fas fa-times"></i>
							</b-button>
						</div>
					</div>
					<div v-else class="text-muted small">Дополнительных контактов нет.</div>
				</section>

				<section class="user-card-section">
					<div class="user-card-section__header">
						<h6>Адреса</h6>
						<b-button
							v-if="!linkedEmployeeFieldsReadOnly && (!isProfile || canEditSelfField('details'))"
							size="sm"
							variant="outline-primary"
							@click="addContactAddress"
						>
							<i class="fas fa-plus mr-1"></i>
							Добавить адрес
						</b-button>
					</div>
					<div v-if="contactAddresses.length" class="employee-address-list">
						<div
							v-for="(address, index) in contactAddresses"
							:key="address.local_id || address.id || index"
							class="employee-address-card"
						>
							<div class="employee-address-card__head">
								<b-form-input
									v-model.trim="address.label"
									placeholder="Подпись адреса"
									:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"
								></b-form-input>
								<b-button
									v-if="!linkedEmployeeFieldsReadOnly && (!isProfile || canEditSelfField('details'))"
									variant="outline-danger"
									title="Удалить адрес"
									@click="removeContactAddress(index)"
								>
									<i class="fas fa-times"></i>
								</b-button>
							</div>
							<b-form-textarea
								v-model.trim="address.formatted"
								rows="2"
								placeholder="Адрес целиком"
								:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"
							></b-form-textarea>
							<div class="employee-address-grid mt-2">
								<b-form-input v-model.trim="address.street" placeholder="Улица" :disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"></b-form-input>
								<b-form-input v-model.trim="address.city" placeholder="Город" :disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"></b-form-input>
								<b-form-input v-model.trim="address.region" placeholder="Регион" :disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"></b-form-input>
								<b-form-input v-model.trim="address.postal_code" placeholder="Индекс" :disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"></b-form-input>
								<b-form-input v-model.trim="address.country" placeholder="Страна" :disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"></b-form-input>
								<b-form-input v-model.trim="address.extended_address" placeholder="Дополнение" :disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"></b-form-input>
							</div>
						</div>
					</div>
					<div v-else class="text-muted small">Адреса не указаны.</div>
				</section>

				<section class="user-card-section">
					<div class="user-card-section__header">
						<h6>Дополнительная информация</h6>
					</div>
					<b-form-group label="Ярлыки" label-for="contactLabels">
						<b-form-input
							id="contactLabels"
							v-model="contactLabelsText"
							placeholder="Через запятую"
							:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"
						></b-form-input>
					</b-form-group>
					<b-form-group label="Заметки" label-for="employeeNotes">
						<AppTextarea
							id="employeeNotes"
							v-model.trim="notes"
							:disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"
						></AppTextarea>
					</b-form-group>
					<div class="user-card-section__header user-card-section__header--sub">
						<strong>Дополнительные поля</strong>
						<b-button
							v-if="!linkedEmployeeFieldsReadOnly && (!isProfile || canEditSelfField('details'))"
							size="sm"
							variant="outline-primary"
							@click="addCustomField"
						>
							<i class="fas fa-plus mr-1"></i>
							Добавить поле
						</b-button>
					</div>
					<div v-if="customFields.length" class="employee-repeatable-list">
						<div
							v-for="(field, index) in customFields"
							:key="field.local_id || index"
							class="employee-repeatable-row employee-repeatable-row--custom"
						>
							<b-form-input v-model.trim="field.label" placeholder="Название" :disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"></b-form-input>
							<b-form-input v-model.trim="field.value" placeholder="Значение" :disabled="linkedEmployeeFieldsReadOnly || (isProfile && !canEditSelfField('details'))"></b-form-input>
							<b-button
								v-if="!linkedEmployeeFieldsReadOnly && (!isProfile || canEditSelfField('details'))"
								variant="outline-danger"
								title="Удалить поле"
								@click="removeCustomField(index)"
							>
								<i class="fas fa-times"></i>
							</b-button>
						</div>
					</div>
					<div v-else class="text-muted small">Дополнительных полей нет.</div>
				</section>

				<section v-if="!isProfile" class="user-card-section">
					<div class="user-card-section__header">
						<h6>Доступ в личный кабинет</h6>
						<b-badge :variant="cabinetAccessEnabled ? 'success' : 'secondary'">
							{{ cabinetAccessEnabled ? 'включен' : 'выключен' }}
						</b-badge>
					</div>
					<b-alert v-if="isEdit && !active" show variant="warning" class="py-2">
						Для отключённого сотрудника доступ в личный кабинет выключен.
					</b-alert>
					<b-alert v-if="!canManageAccess" show variant="secondary" class="py-2">
						Изменять статус и доступ в личный кабинет может только администратор.
					</b-alert>
					<b-form-checkbox
						v-model="cabinetAccessEnabled"
						:disabled="!canManageAccess || (isEdit && !active)"
						switch
					>
						Доступ в личный кабинет
					</b-form-checkbox>

					<div v-if="cabinetAccessEnabled && (!isEdit || active)" class="user-card-cabinet">
						<b-form-group label="Логин" label-for="cabinetLogin">
							<b-form-input
								id="cabinetLogin"
								v-model.trim="login"
								:state="loginState"
								:disabled="!canManageAccess"
								placeholder="Введите логин"
								@change="onLoginFieldChange"
							></b-form-input>
							<span class="small text-danger" v-if="!loginAvailable">Данный логин занят</span>
						</b-form-group>
						<b-form-group label="Роли">
							<b-form-select
								v-model="selectedRoles"
								:options="roleOptions"
								multiple
								:select-size="roleSelectSize"
								:disabled="!canManageAccess"
							></b-form-select>
						</b-form-group>
						<b-form-group :label="hasPassword ? 'Новый пароль' : 'Пароль'" label-for="cabinetPassword">
							<b-input-group>
								<b-form-input
									id="cabinetPassword"
									v-model="password"
									:type="passwordInputType"
									:state="passwordState"
									:disabled="!canManageAccess"
									autocomplete="new-password"
									placeholder="Введите или сгенерируйте пароль"
									@input="onPasswordInput"
								></b-form-input>
								<b-input-group-append>
									<b-button
										variant="outline-secondary"
										:disabled="!canManageAccess"
										@click="togglePasswordVisibility"
									>
										<i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
									</b-button>
									<b-button
										variant="outline-primary"
										:disabled="!canManageAccess"
										@click="generatePassword"
									>
										<i class="fas fa-random"></i>
									</b-button>
								</b-input-group-append>
							</b-input-group>
							<small class="text-muted" v-if="hasPassword">
								Оставьте поле пустым, если пароль менять не нужно.
							</small>
						</b-form-group>
						</div>
					<AccountSecurity v-if="isEdit && userId > 0 && (isProfile || ($store.getters.currentUser.roles || []).includes('Admin'))" :user-id="userId" :admin="($store.getters.currentUser.roles || []).includes('Admin')"/>
					</section>
					</template>
					<AccountSecurity v-if="isProfile && isEditingForm && isEdit && userId > 0 && (isProfile || ($store.getters.currentUser.roles || []).includes('Admin'))" :user-id="userId" :admin="($store.getters.currentUser.roles || []).includes('Admin')"/>

					<section v-if="isProfile" class="user-card-section profile-navigation-preference">
						<div class="user-card-section__header">
							<div>
								<h6>Моя четвёртая вкладка</h6>
								<div class="small text-muted mt-1">
									Выбранный раздел появится в основной навигации и станет главным действием на домашнем экране.
								</div>
							</div>
							<i class="fas fa-thumbtack text-primary"></i>
						</div>
						<div class="profile-navigation-preference__controls">
							<div class="profile-navigation-preference__current">
								<span class="profile-navigation-preference__current-icon" aria-hidden="true">
									<i :class="preferredWorkspaceSelection ? preferredWorkspaceSelection.icon : 'fas fa-users'"></i>
								</span>
								<span class="profile-navigation-preference__current-copy">
									<small>{{ workspaceChoiceTouched && preferredWorkspaceChanged ? 'Новый выбор · ещё не сохранён' : preferredWorkspaceUnavailable ? 'Сохранённый раздел сейчас недоступен' : 'Сейчас выбрано' }}</small>
									<strong>{{ preferredWorkspaceSelection ? preferredWorkspaceSelection.label : 'По настройке отдела' }}</strong>
								</span>
								<button type="button" class="profile-navigation-preference__choose"
									:disabled="navigationPreferenceLoading || navigationPreferenceSaving"
									:aria-expanded="workspacePickerOpen ? 'true' : 'false'"
									aria-controls="preferred-workspace-picker"
									@click="toggleWorkspacePicker">
									{{ workspacePickerOpen ? 'Скрыть' : 'Выбрать' }}
									<i :class="workspacePickerOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
								</button>
							</div>
							<b-button
								variant="primary"
								:disabled="navigationPreferenceLoading || navigationPreferenceSaving || !preferredWorkspaceChanged"
								@click="savePreferredWorkspace"
							>
								<b-spinner v-if="navigationPreferenceSaving" small class="mr-1"></b-spinner>
								{{ navigationPreferenceSaving ? 'Сохраняем…' : 'Сохранить выбор' }}
							</b-button>
						</div>
						<div v-if="navigationPreferenceError" class="profile-navigation-preference__feedback profile-navigation-preference__feedback--error" role="alert">{{ navigationPreferenceError }}</div>
						<div v-else-if="navigationPreferenceSaved" class="profile-navigation-preference__feedback profile-navigation-preference__feedback--saved" role="status">Четвёртая кнопка сохранена.</div>
						<div v-show="workspacePickerOpen" id="preferred-workspace-picker" class="profile-navigation-preference__picker">
							<label class="profile-navigation-preference__search-label" for="preferred-workspace-search">Найти раздел</label>
							<input id="preferred-workspace-search" ref="workspaceSearch" v-model.trim="workspaceSearch"
								type="search" class="form-control profile-navigation-preference__search"
								placeholder="Например, табели или заявки" :disabled="navigationPreferenceLoading || navigationPreferenceSaving">
							<div class="profile-navigation-preference__choices" role="radiogroup" aria-label="Рабочий раздел четвёртой кнопки">
								<label v-if="!workspaceSearch" class="profile-navigation-preference__choice"
									:class="{'is-selected': preferredWorkspaceKey === null}">
									<input v-model="preferredWorkspaceKey" type="radio" name="preferred-workspace" :value="null"
										:disabled="navigationPreferenceLoading || navigationPreferenceSaving" @change="onWorkspaceChoice">
									<span class="profile-navigation-preference__choice-icon"><i class="fas fa-users" aria-hidden="true"></i></span>
									<span class="profile-navigation-preference__choice-label"><strong>Как настроено в отделе</strong><small>Использовать рабочий раздел отдела</small></span>
									<i class="fas fa-check-circle profile-navigation-preference__check" aria-hidden="true"></i>
								</label>
								<div v-for="group in filteredWorkspaceGroups" :key="group.name" class="profile-navigation-preference__group">
									<h6>{{ group.name }}</h6>
									<div class="profile-navigation-preference__group-items">
										<label v-for="workspace in group.items" :key="workspace.key" class="profile-navigation-preference__choice"
											:class="{'is-selected': preferredWorkspaceKey === workspace.key}">
											<input v-model="preferredWorkspaceKey" type="radio" name="preferred-workspace" :value="workspace.key"
												:disabled="navigationPreferenceLoading || navigationPreferenceSaving" @change="onWorkspaceChoice">
											<span class="profile-navigation-preference__choice-icon"><i :class="workspace.icon" aria-hidden="true"></i></span>
											<span class="profile-navigation-preference__choice-label"><strong>{{ workspace.label }}</strong><small>{{ workspace.shortLabel }}</small></span>
											<i class="fas fa-check-circle profile-navigation-preference__check" aria-hidden="true"></i>
										</label>
									</div>
								</div>
								<p v-if="!filteredWorkspaceGroups.length" class="profile-navigation-preference__empty">Раздел не найден. Попробуйте другое название.</p>
							</div>
						</div>
						<div class="small text-muted mt-2">
							Здесь показаны доступные вам разделы. Выбор вступит в силу после сохранения.
						</div>
					</section>

					<NotificationDeviceSettings v-if="isProfile"/>

					<section v-if="canShowDangerZone" class="user-card-section user-danger-zone">
					<div class="user-card-section__header">
						<div>
							<h6>Опасная зона</h6>
							<div class="small text-muted mt-1">
								Безвозвратное удаление доступно только для записи без рабочих связей.
							</div>
						</div>
						<i class="fas fa-exclamation-triangle text-danger"></i>
					</div>

					<div v-if="deletionImpactLoading" class="text-muted py-2">
						<b-spinner small class="mr-2"></b-spinner>
						Проверка связанных данных
					</div>
					<b-alert v-else-if="deletionImpactError" show variant="danger" class="py-2">
						{{ deletionImpactError }} Физическое удаление заблокировано.
					</b-alert>
					<template v-else-if="deletionImpact">
						<b-alert v-if="deletionBlockers.length" show variant="warning" class="py-2">
							Физическое удаление недоступно. Оставьте сотрудника отключённым.
						</b-alert>
						<ul v-if="deletionBlockers.length" class="user-impact-list mb-3">
							<li v-for="blocker in deletionBlockers" :key="blocker.label">
								<span>{{ blocker.label }}</span>
								<b-badge variant="secondary">{{ blocker.count }}</b-badge>
							</li>
						</ul>
						<b-alert v-else show variant="success" class="py-2">
							Рабочих и исторических связей не найдено. Запись можно удалить безвозвратно.
						</b-alert>
					</template>

					<div class="d-flex flex-wrap justify-content-end">
						<b-button
							v-if="deletionImpactError"
							size="sm"
							variant="outline-secondary"
							class="mr-2"
							@click="loadDeletionImpact"
						>
							Повторить проверку
						</b-button>
						<b-button
							size="sm"
							variant="danger"
							:disabled="!canDeletePermanently || deletionImpactLoading"
							@click="openPermanentDeleteModal"
						>
							<i class="fas fa-trash-alt mr-1"></i>
							{{ canDeletePermanently ? 'Удалить безвозвратно' : 'Удаление недоступно' }}
						</b-button>
					</div>
				</section>
			</div>

			<aside v-if="isEdit && canViewHistory" class="user-card-history">
				<div class="user-card-section user-card-history__panel">
					<div class="user-card-section__header">
						<h6>История изменений</h6>
						<b-button size="sm" variant="outline-secondary" @click="loadHistory">
							<i class="fas fa-sync-alt"></i>
						</b-button>
					</div>
					<div v-if="historyLoading" class="text-center text-muted py-4">
						<b-spinner small class="mr-2"></b-spinner>
						Загрузка истории
					</div>
					<div v-else-if="history.length" class="user-history-list">
						<div v-for="item in history" :key="item.id" class="user-history-item">
							<div class="user-history-item__head">
								<div>
									<b-badge :variant="historyVariant(item.action)">
										{{ historyActionLabel(item.action) }}
									</b-badge>
									<div class="user-history-item__actor">
										{{ historyActor(item) }}
									</div>
								</div>
								<small class="text-muted">{{ formatDate(item.created_at) }}</small>
							</div>
							<div v-if="item.description" class="user-history-item__description">
								{{ item.description }}
							</div>
							<div v-if="historyChanges(item).length" class="user-history-changes">
								<div
									v-for="change in historyChanges(item)"
									:key="`${item.id}-${change.field}`"
									class="user-history-change"
								>
									<div class="user-history-change__label">{{ change.label }}</div>
									<div class="user-history-change__value">
										<span>{{ change.old_value }}</span>
										<i class="fas fa-arrow-right mx-1"></i>
										<span>{{ change.new_value }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-else class="text-muted text-center py-4">
						Истории изменений пока нет
					</div>
				</div>
			</aside>
		</div>

		<b-modal
			v-model="showOrganizationModal"
			title="Новая организация-работодатель"
			hide-footer
			:no-close-on-backdrop="organizationSaving"
			:no-close-on-esc="organizationSaving"
		>
			<b-alert v-if="organizationError" show variant="danger" class="py-2">
				{{ organizationError }}
			</b-alert>
			<b-form-group label="Полное название" label-for="organizationName">
				<b-form-input id="organizationName" v-model.trim="organizationForm.name"></b-form-input>
			</b-form-group>
			<b-form-group label="Краткое название" label-for="organizationShortName">
				<b-form-input id="organizationShortName" v-model.trim="organizationForm.short_name"></b-form-input>
			</b-form-group>
			<b-form-group label="Другие варианты написания" label-for="organizationAliases">
				<b-form-input
					id="organizationAliases"
					v-model.trim="organizationForm.aliases"
					placeholder="Через запятую"
				></b-form-input>
				<small class="text-muted">
					Например, варианты из Google Контактов или будущей выгрузки 1С.
				</small>
			</b-form-group>
			<div class="d-flex justify-content-end">
				<b-button variant="outline-secondary" class="mr-2" :disabled="organizationSaving" @click="showOrganizationModal = false">
					Отмена
				</b-button>
				<b-button variant="primary" :disabled="organizationSaving || !organizationForm.name" @click="createOrganization">
					<b-spinner v-if="organizationSaving" small class="mr-1"></b-spinner>
					Создать
				</b-button>
			</div>
		</b-modal>

		<b-modal
			v-model="showStatusModal"
			:title="statusTargetActive ? 'Включить учётную запись' : 'Отключить учётную запись'"
			hide-footer
			:no-close-on-backdrop="statusSubmitting"
			:no-close-on-esc="statusSubmitting"
			@hidden="resetStatusModal"
		>
			<div v-if="statusImpactLoading" class="text-center text-muted py-4">
				<b-spinner small class="mr-2"></b-spinner>
				Проверка последствий
			</div>
			<template v-else>
				<b-alert v-if="statusError" show variant="danger" class="py-2">
					{{ statusError }}
				</b-alert>
				<template v-if="statusImpact && !statusTargetActive">
					<p>
						Отключить учётную запись <strong>{{ fullName || 'сотрудника' }}</strong> (#{{ userId }})?
					</p>
					<b-alert show variant="warning" class="py-2">
						Доступ в личный кабинет и все активные сессии будут отключены. История сотрудника сохранится.
					</b-alert>
					<b-alert v-if="statusImpact.assignment_check_failed" show variant="warning" class="py-2">
						Не удалось полностью проверить текущие назначения. Это не мешает деактивации — проверьте назначения вручную.
					</b-alert>
					<div v-if="statusImpact.active_assignments && statusImpact.active_assignments.length">
						<div class="font-weight-bold mb-2">Текущие назначения останутся в данных:</div>
						<ul class="user-impact-list">
							<li v-for="item in statusImpact.active_assignments" :key="item.label">
								<span>{{ item.label }}</span>
								<b-badge variant="secondary">{{ item.count }}</b-badge>
							</li>
						</ul>
					</div>
				</template>
				<template v-else-if="statusImpact && statusTargetActive">
					<p>
						Включить учётную запись <strong>{{ fullName || 'сотрудника' }}</strong> (#{{ userId }})?
					</p>
					<b-alert v-if="statusImpact.will_restore_cabinet_access" show variant="success" class="py-2">
						Учётная запись раньше имела доступ в личный кабинет — доступ будет восстановлен сразу.
					</b-alert>
					<b-alert v-else-if="statusImpact.cabinet_restore_missing_credentials" show variant="warning" class="py-2">
						Учётная запись станет активной, но личный кабинет останется выключен: логин или пароль отсутствует.
					</b-alert>
					<b-alert v-else show variant="secondary" class="py-2">
						Учётная запись станет активной. Личный кабинет останется выключен.
					</b-alert>
				</template>

				<div class="d-flex justify-content-end mt-3">
					<b-button variant="outline-secondary" class="mr-2" :disabled="statusSubmitting" @click="showStatusModal = false">
						Отмена
					</b-button>
					<b-button
						v-if="statusImpact"
						:variant="statusTargetActive ? 'success' : 'warning'"
						:disabled="statusSubmitting"
						@click="applyStatusChange"
					>
						<b-spinner v-if="statusSubmitting" small class="mr-1"></b-spinner>
						{{ statusTargetActive ? 'Сделать действующим' : 'Отключить' }}
					</b-button>
				</div>
			</template>
		</b-modal>

		<b-modal
			v-model="showPermanentDeleteModal"
			title="Безвозвратное удаление сотрудника"
			hide-footer
			:no-close-on-backdrop="deletionSubmitting"
			:no-close-on-esc="deletionSubmitting"
			@hidden="resetPermanentDeleteModal"
		>
			<b-alert show variant="danger">
				<strong>Действие необратимо.</strong> Запись сотрудника нельзя будет восстановить, а её идентификатор исчезнет из системы.
			</b-alert>
			<p class="mb-2">
				Сотрудник: <strong>{{ fullName || 'Без имени' }}</strong> (#{{ userId }})
			</p>
			<p class="text-success font-weight-bold">
				Проверка завершена: рабочих и исторических связей не найдено.
			</p>
			<div v-if="deletionImpact && deletionImpact.service_cleanup && deletionImpact.service_cleanup.length" class="mb-3">
				<div class="font-weight-bold mb-1">Вместе с записью будут удалены:</div>
				<ul class="mb-0">
					<li v-for="item in deletionImpact.service_cleanup" :key="item">{{ item }}</li>
				</ul>
			</div>
			<b-alert show variant="warning" class="py-2">
				Если удаление не является обязательным, безопаснее оставить сотрудника отключённым.
			</b-alert>
			<b-form-checkbox v-model="deletionAcknowledged" :disabled="deletionSubmitting" class="mb-3">
				Я понимаю риски и подтверждаю безвозвратное удаление.
			</b-form-checkbox>
			<b-alert v-if="permanentDeleteError" show variant="danger" class="py-2">
				{{ permanentDeleteError }}
			</b-alert>
			<div class="d-flex flex-wrap justify-content-end">
				<b-button variant="outline-secondary" class="mr-2" :disabled="deletionSubmitting" @click="showPermanentDeleteModal = false">
					Оставить отключённым
				</b-button>
				<b-button variant="danger" :disabled="!deletionAcknowledged || deletionSubmitting" @click="deletePermanently">
					<b-spinner v-if="deletionSubmitting" small class="mr-1"></b-spinner>
					Удалить безвозвратно
				</b-button>
			</div>
		</b-modal>
		<b-modal
			v-model="showAccountGoogleContactModal"
			title="Выберите контакт Google"
			size="lg"
			ok-title="Подставить в форму"
			cancel-title="Отмена"
			:ok-disabled="!selectedAccountGoogleContact"
			@ok="applyAccountGoogleContact"
		>
			<b-alert v-if="accountGooglePreviewError" show variant="danger">
				{{ accountGooglePreviewError }}
			</b-alert>
			<div class="account-google-contact-list">
				<label
					v-for="row in accountGoogleContactRows"
					:key="row.decision_key"
					:class="[
						'account-google-contact',
						{'account-google-contact--selected': selectedAccountGoogleContactKey === row.decision_key}
					]"
				>
					<input
						v-model="selectedAccountGoogleContactKey"
						type="radio"
						:value="row.decision_key"
					>
					<span>
						<strong>{{ row.source_display_name || 'Без имени' }}</strong>
						<small>
							{{ accountGoogleContactSummary(row) }}
						</small>
						<small v-if="row.matched_employee" class="text-success">
							Совпала с карточкой #{{ row.matched_employee.employee_id }}
							{{ row.matched_employee.full_name }}
						</small>
					</span>
				</label>
			</div>
		</b-modal>
	</div>
</template>

<script>
import {getAvailableWorkspaces} from '@/utils/workspaceRegistry';
import NotificationDeviceSettings from '@/components/NotificationDeviceSettings';
import PhoneInput from '@/components/PhoneInput.vue';
import AccountSecurity from '@/components/Security/AccountSecurity.vue';
import api from '@/utils/api';
import {
	formatPhone,
	formatPhoneInput,
	normalizePhoneForStorage,
	phoneHref,
	phoneValidationError,
	telegramPhoneHref
} from '@/utils/phone';

const gmailRegexp = new RegExp(`^[a-z0-9](\\.?[a-z0-9]){5,}@g(oogle)?mail\\.com$`);
const gmailErrorMessage = 'Введите корректный Gmail.';

function normalizeGmailForStorage(value) {
	if (value === null || typeof value === 'undefined') {
		return null;
	}
	const normalized = String(value).trim().toLowerCase();
	if (!normalized) {
		return null;
	}
	if (!gmailRegexp.test(normalized)) {
		throw new Error(gmailErrorMessage);
	}
	return normalized;
}

export default {
	name: 'UserCard',
	components: {
		AccountSecurity,
		NotificationDeviceSettings,
		PhoneInput
	},
	data() {
		return {
			loading: false,
			saving: false,
			historyLoading: false,
			errorMessage: null,
			history: [],
			historyAccessDenied: false,
			canViewHistoryKnown: false,
			canViewHistoryFlag: false,
			directorySettings: null,
			editMode: false,
			profileUserId: null,
			employeeCardId: null,
			loadedFields: {},
			firstName: null,
			isBot: false,
			lastName: null,
			middleName: null,
			position: null,
			birthDate: null,
			employmentOrganizationId: null,
			employmentOrganization: null,
			employmentOrganizations: [],
			notes: null,
			officeRoom: null,
			contactLabels: [],
			customFields: [],
			contactMethods: [],
			contactAddresses: [],
			externalSources: [],
			externalProfiles: [],
			phone: null,
			telegramChatId: null,
			telegramUsername: null,
			telegramUrl: null,
			avatarUrl: null,
			avatarSource: 'telegram',
			active: true,
			gmail: null,
			selectedDepartments: [],
			cabinetAccessEnabled: false,
			login: null,
			originalLogin: null,
			password: null,
			passwordTouched: false,
			showPassword: false,
			hasPassword: false,
			loginAvailable: true,
			selectedRoles: [],
			preferredWorkspaceKey: null,
			preferredWorkspaceOriginalKey: null,
			workspacePickerOpen: false,
			workspaceSearch: '',
			workspaceChoiceTouched: false,
			navigationPreferenceLoading: false,
			navigationPreferenceSaving: false,
			navigationPreferenceError: null,
			navigationPreferenceSaved: false,
			showStatusModal: false,
			statusImpactLoading: false,
			statusSubmitting: false,
			statusImpact: null,
			statusError: null,
			deletionImpactLoading: false,
			deletionImpact: null,
			deletionImpactError: null,
			showPermanentDeleteModal: false,
			deletionAcknowledged: false,
			deletionSubmitting: false,
			permanentDeleteError: null,
			exporting: false,
			nextLocalRowId: 1,
			showOrganizationModal: false,
			organizationSaving: false,
			organizationError: null,
			organizationForm: {
				name: '',
				short_name: '',
				aliases: ''
			},
			accountEmployeeMode: 'existing',
			accountCandidates: [],
			accountCandidatesLoading: false,
			selectedEmployeeCandidate: null,
			confirmUnverifiedEmployee: false,
			accountEmployeeDuplicates: [],
			accountEmployeeDuplicateConfirmed: false,
			accountGooglePreviewLoading: false,
			accountGooglePreviewError: '',
			accountGoogleContactRows: [],
			selectedAccountGoogleContactKey: null,
			showAccountGoogleContactModal: false
		};
	},
	computed: {
		isProfile() {
			return this.$route.name === 'profile';
		},
		isCreate() {
			return !this.isProfile && !(Number.isInteger(this.userId) && this.userId > 0);
		},
		userId() {
			return this.isProfile ? Number(this.profileUserId) : Number(this.$route.params.id);
		},
		isEdit() {
			return this.isProfile || (Number.isInteger(this.userId) && this.userId > 0);
		},
		isEditingForm() {
			return this.isCreate || this.editMode;
		},
		linkedEmployeeFieldsReadOnly() {
			if (this.isProfile) return false;
			if (this.isEdit) return true;
			return this.accountEmployeeMode === 'existing'
				&& Boolean(this.selectedEmployeeCandidate);
		},
		accountCandidateOptions() {
			return this.accountCandidates.map(employee => {
				const period = employee.latest_employment_period || {};
				const contacts = (employee.contact_methods || [])
					.map(contact => contact.value)
					.filter(Boolean);
				return Object.assign({}, employee, {
					label: employee.full_name || employee.display_name,
					search_text: [
						employee.full_name,
						employee.display_name,
						period.personnel_number,
						contacts.join(' ')
					].filter(Boolean).join(' ')
				});
			});
		},
		selectedAccountGoogleContact() {
			return this.accountGoogleContactRows.find(row => (
				row.decision_key === this.selectedAccountGoogleContactKey
			)) || null;
		},
		selectedEmployeeNeedsConfirmation() {
			return Boolean(
				this.isCreate
				&& this.accountEmployeeMode === 'existing'
				&& this.selectedEmployeeCandidate
				&& this.selectedEmployeeCandidate.lifecycle_status === 'unverified'
			);
		},
		canManageAccess() {
			return !this.isProfile && this.$store.getters.isAdmin === true;
		},
		isCurrentUser() {
			const currentUser = this.$store.getters.currentUser;
			return Boolean(currentUser && Number(currentUser.userId) === Number(this.userId));
		},
		canDeleteEmployees() {
			return !this.isProfile && this.$can('delete', 'users');
		},
		canShowDangerZone() {
			return this.isEdit && !this.isEditingForm && !this.active &&
				this.canDeleteEmployees && !this.isCurrentUser;
		},
		statusTargetActive() {
			if (this.statusImpact && typeof this.statusImpact.target_active === 'boolean') {
				return this.statusImpact.target_active;
			}
			return !this.active;
		},
		deletionBlockers() {
			return this.deletionImpact && Array.isArray(this.deletionImpact.blockers)
				? this.deletionImpact.blockers
				: [];
		},
		canDeletePermanently() {
			return Boolean(this.deletionImpact && this.deletionImpact.can_delete && !this.deletionBlockers.length);
		},
		canSave() {
			if (this.isProfile) {
				return true;
			}
			return this.isEdit
				? (this.$can('update', 'users') || Boolean(this.directorySettings && this.directorySettings.can_edit))
				: (this.$can('create', 'users') || Boolean(this.directorySettings && this.directorySettings.can_edit));
		},
		canReadRawUsers() {
			return this.$can('read', 'users');
		},
		canImportExportEmployees() {
			return Boolean(this.directorySettings && this.directorySettings.can_import_export) ||
				this.$store.getters.isAdmin === true;
		},
		canViewHistory() {
			if (!this.isEdit || this.historyAccessDenied) {
				return false;
			}
			if (this.canViewHistoryKnown) {
				return this.canViewHistoryFlag;
			}
			if (this.isProfile) {
				return true;
			}
			const visibility = this.directorySettings &&
				this.directorySettings.visibility_config &&
				this.directorySettings.visibility_config.history;
			if (visibility === 'all_authenticated') {
				return true;
			}
			if (visibility === 'users_read') {
				return this.canReadRawUsers || this.$store.getters.isAdmin === true;
			}
			if (visibility === 'admin') {
				return this.$store.getters.isAdmin === true;
			}
			return false;
		},
		pageTitle() {
			if (this.isProfile) {
				return 'Мой профиль';
			}
			if (!this.isEdit) {
				return 'Создание учётной записи';
			}
			return this.fullName
				? `${this.fullName} — учётная запись`
				: 'Редактирование учётной записи';
		},
		fullName() {
			return [this.lastName, this.firstName].filter(Boolean).join(' ').trim();
		},
		cardDisplayName() {
			return [this.lastName, this.firstName, this.middleName].filter(Boolean).join(' ').trim();
		},
		employmentOrganizationName() {
			if (this.employmentOrganization && this.employmentOrganization.name) {
				return this.employmentOrganization.name;
			}
			const option = this.employmentOrganizations.find(item => {
				return Number(item.id) === Number(this.employmentOrganizationId);
			});
			return option ? option.name : null;
		},
		employmentOrganizationOptions() {
			return [{value: null, text: 'Не указана'}].concat(
				this.employmentOrganizations.map(item => ({
					value: item.id,
					text: item.short_name ? `${item.name} (${item.short_name})` : item.name
				}))
			);
		},
		contactTypeOptions() {
			return [
				{value: 'phone', text: 'Телефон'},
				{value: 'email', text: 'Email'}
			];
		},
		visiblePhoneContacts() {
			const result = [];
			if (this.phone) {
				result.push({
					key: 'primary-phone',
					value: formatPhone(this.phone),
					label: 'Основной · Telegram'
				});
			}
			this.contactMethods
				.filter(item => item && item.contact_type === 'phone' && item.value)
				.forEach((item, index) => result.push({
					key: item.id || `phone-${index}`,
					value: formatPhone(item.value),
					label: item.label || 'Телефон'
				}));
			return result;
		},
		visibleEmailContacts() {
			const result = [];
			if (this.gmail) {
				result.push({
					key: 'system-gmail',
					value: this.gmail,
					label: 'Системный Gmail'
				});
			}
			this.contactMethods
				.filter(item => item && item.contact_type === 'email' && item.value)
				.forEach((item, index) => result.push({
					key: item.id || `email-${index}`,
					value: item.value,
					label: item.label || 'Электронная почта'
				}));
			return result;
		},
		hasVisibleContacts() {
			return Boolean(
				this.visiblePhoneContacts.length
				|| this.visibleEmailContacts.length
				|| this.contactAddresses.length
			);
		},
		hasVisibleDetails() {
			return Boolean(
				this.middleName
				|| this.birthDate
				|| this.officeRoom
				|| this.employmentOrganizationName
				|| this.notes
				|| this.contactLabels.length
				|| this.customFields.length
				|| this.externalSources.length
				|| this.externalProfiles.some(profile => profile && profile.fields && profile.fields.length)
			);
		},
		contactLabelsText: {
			get() {
				return this.contactLabels.join(', ');
			},
			set(value) {
				const seen = new Set();
				this.contactLabels = String(value || '').split(',').map(item => item.trim()).filter(item => {
					const key = item.toLowerCase();
					if (!key || seen.has(key)) {
						return false;
					}
					seen.add(key);
					return true;
				});
			}
		},
		avatarInitials() {
			const first = this.firstName ? this.firstName.charAt(0) : '';
			const last = this.lastName ? this.lastName.charAt(0) : '';
			return `${last}${first}`.trim() || '?';
		},
		avatarSourceOptions() {
			return [
				{value: 'manual', text: 'Выбрать вручную'},
				{value: 'telegram', text: 'Синхронизировать с Telegram'}
			];
		},
		avatarSourceLabel() {
			return this.avatarSource === 'telegram' ? 'Telegram' : 'Вручную';
		},

		preferredWorkspaceSelection() {
			return getAvailableWorkspaces(this).find(workspace => workspace.key === this.preferredWorkspaceKey) || null;
		},
		preferredWorkspaceUnavailable() {
			return Boolean(this.preferredWorkspaceOriginalKey && !getAvailableWorkspaces(this).some(workspace =>
				workspace.key === this.preferredWorkspaceOriginalKey));
		},
		filteredWorkspaceGroups() {
			const search = String(this.workspaceSearch || '').trim().toLocaleLowerCase('ru-RU');
			const groups = [];
			getAvailableWorkspaces(this).forEach(workspace => {
				if (search && !`${workspace.label} ${workspace.shortLabel} ${workspace.group}`.toLocaleLowerCase('ru-RU').includes(search)) return;
				let group = groups.find(item => item.name === workspace.group);
				if (!group) {
					group = {name: workspace.group, items: []};
					groups.push(group);
				}
				group.items.push(workspace);
			});
			return groups;
		},
		preferredWorkspaceChanged() {
			return (this.preferredWorkspaceKey || null) !== (this.preferredWorkspaceOriginalKey || null);
		},
		telegramLink() {
			if (this.telegramUrl) {
				return this.telegramUrl;
			}
			if (this.telegramUsername) {
				return `https://telegram.me/${String(this.telegramUsername).replace(/^@+/, '')}`;
			}
			return telegramPhoneHref(this.phone);
		},
		selfEditConfig() {
			return (this.directorySettings && this.directorySettings.self_edit_config) || {};
		},
		canEditAvatar() {
			return this.isEditingForm && this.isEdit && (this.isProfile ? this.canEditSelfField('avatar') : this.canSave);
		},
		canEditAvatarSource() {
			return this.isEditingForm && (this.isProfile ? this.canEditSelfField('avatar_source') : this.canSave);
		},
		canEditCurrentCard() {
			if (!this.isEdit) {
				return false;
			}
			if (!this.isProfile) {
				return this.canSave;
			}
			return Object.keys(this.selfEditConfig).some(key => this.selfEditConfig[key]);
		},
		canEditAnySelfContact() {
			return this.canEditSelfField('phone') || this.canEditSelfField('gmail');
		},
		canChangeSelfContactType() {
			return this.canEditSelfField('phone') && this.canEditSelfField('gmail');
		},
		departmentsText() {
			return this.selectedDepartments
				.map(id => {
					const department = this.departments.find(item => Number(item.id) === Number(id));
					return department ? department.name : null;
				})
				.filter(Boolean)
				.join(', ');
		},
		rolesText() {
			return this.selectedRoles
				.map(id => {
					const role = this.roleOptions.find(item => Number(item.value) === Number(id));
					return role ? role.text : null;
				})
				.filter(Boolean)
				.join(', ');
		},
		isEmailValid() {
			return !this.gmailValidationError;
		},
		gmailErrorMessage() {
			return gmailErrorMessage;
		},
		gmailValidationError() {
			try {
				normalizeGmailForStorage(this.gmail);
				return null;
			} catch (e) {
				return e.message || gmailErrorMessage;
			}
		},
		gmailState() {
			if (!this.gmail) {
				return null;
			}
			if (this.isProfile && !this.canEditSelfField('gmail')) {
				return null;
			}
			return this.gmailValidationError ? false : true;
		},
		phoneValidationError() {
			return phoneValidationError(this.phone);
		},
		contactMethodsPhoneValidationError() {
			const invalidIndex = this.contactMethods.findIndex(method => (
				method
				&& method.contact_type === 'phone'
				&& phoneValidationError(method.value, {mode: 'contact'})
			));
			return invalidIndex === -1
				? null
				: `Проверьте дополнительный телефон №${invalidIndex + 1}.`;
		},
		departments() {
			return this.$store.state.departments || [];
		},
		departmentOptions() {
			return this.departments.map(dept => ({
				value: dept.id,
				text: dept.name
			}));
		},
		roleOptions() {
			return (this.$store.state.roles || []).map(role => ({
				value: role.role_id,
				text: role.role_name
			}));
		},
		roleSelectSize() {
			return Math.min(Math.max(this.roleOptions.length, 3), 8);
		},
		passwordInputType() {
			return this.showPassword ? 'text' : 'password';
		},
		passwordRequired() {
			return this.canManageAccess && this.cabinetAccessEnabled && !this.hasPassword;
		},
		passwordState() {
			if (!this.cabinetAccessEnabled || !this.passwordRequired) {
				return null;
			}
			return Boolean(this.passwordTouched && this.password);
		},
		loginState() {
			if (!this.cabinetAccessEnabled || !this.canManageAccess) {
				return null;
			}
			return Boolean(this.login) && this.loginAvailable;
		},
		isSubmitDisabled() {
			const shouldValidateGmail = !this.isProfile || this.canEditSelfField('gmail');
			const shouldValidatePhone = !this.isProfile || this.canEditSelfField('phone');
			if (!this.canSave || this.loading || this.saving || this.statusImpactLoading || this.statusSubmitting) {
				return true;
			}
			if (shouldValidateGmail && !this.isEmailValid) {
				return true;
			}
			if (shouldValidatePhone && this.phoneValidationError) {
				return true;
			}
			if (this.contactMethodsPhoneValidationError) {
				return true;
			}
			if (this.isProfile) {
				return false;
			}
			if (
				this.isCreate
				&& this.accountEmployeeMode === 'existing'
				&& !this.selectedEmployeeCandidate
			) {
				return true;
			}
			if (
				this.selectedEmployeeNeedsConfirmation
				&& !this.confirmUnverifiedEmployee
			) {
				return true;
			}
			if (!this.canManageAccess || !this.cabinetAccessEnabled) {
				return false;
			}
			if (this.isEdit && !this.active) {
				return false;
			}
			if (!this.login || !this.loginAvailable) {
				return true;
			}
			return this.passwordRequired && !(this.passwordTouched && this.password);
		}
	},
	watch: {
		'$route.params.id'() {
			this.load();
		},
		'$route.name'() {
			this.load();
		},
		lastName() {
			this.resetAccountDuplicateConfirmation();
		},
		firstName() {
			this.resetAccountDuplicateConfirmation();
		},
		birthDate() {
			this.resetAccountDuplicateConfirmation();
		},
		accountEmployeeMode(value) {
			if (!this.isCreate) return;
			this.accountEmployeeDuplicates = [];
			this.accountEmployeeDuplicateConfirmed = false;
			this.confirmUnverifiedEmployee = false;
			if (value !== 'new') return;
			this.selectedEmployeeCandidate = null;
			this.accountGooglePreviewLoading = false;
			this.accountGooglePreviewError = '';
			this.accountGoogleContactRows = [];
			this.selectedAccountGoogleContactKey = null;
			this.showAccountGoogleContactModal = false;
			this.clearEmployeePrefill();
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		formatPhone,
		phoneHref,
		resetAccountDuplicateConfirmation() {
			if (!this.isCreate || this.accountEmployeeMode !== 'new') return;
			this.accountEmployeeDuplicates = [];
			this.accountEmployeeDuplicateConfirmed = false;
		},
		async load() {
			this.loading = true;
			this.errorMessage = null;
			this.deletionImpact = null;
			this.deletionImpactError = null;
			try {
				await Promise.all([
					this.$store.dispatch('getDepartments'),
					this.$store.dispatch('getRoles'),
					this.loadDirectorySettings(),
					this.loadEmploymentOrganizations()
				]);
				if (this.isProfile) {
					const profile = await this.$store.dispatch('getMyProfile');
					this.directorySettings = profile.settings || this.directorySettings;
					this.profileUserId = profile.user && profile.user.user_id;
					this.fillForm(profile.user || {});
					this.editMode = false;
					await this.loadHistoryIfVisible();
					await this.loadNavigationPreferences();
					return;
				}
				if (this.isEdit) {
					if (!(this.directorySettings && this.directorySettings.can_list) && !this.$store.getters.isAdmin) {
						this.errorMessage = 'Нет доступа к разделу «Учётные записи».';
						return;
					}
					const user = await this.loadUserForCard();
					this.fillForm(user);
					this.editMode = false;
					await this.loadHistoryIfVisible();
					if (this.canShowDangerZone) {
						await this.loadDeletionImpact();
					}
				} else {
					this.resetForm();
					if (!(this.directorySettings && this.directorySettings.can_list) && !this.$store.getters.isAdmin) {
						this.errorMessage = 'Нет доступа к разделу «Учётные записи».';
						return;
					}
					this.editMode = true;
					await this.loadAccountCandidates();
				}
			} catch (e) {
				this.errorMessage = this.errorText(e);
			} finally {
				this.loading = false;
			}
		},
		resetForm() {
			this.profileUserId = null;
			this.employeeCardId = null;
			this.firstName = null;
			this.isBot = false;
			this.lastName = null;
			this.middleName = null;
			this.position = null;
			this.birthDate = null;
			this.employmentOrganizationId = null;
			this.employmentOrganization = null;
			this.notes = null;
			this.officeRoom = null;
			this.contactLabels = [];
			this.customFields = [];
			this.contactMethods = [];
			this.contactAddresses = [];
			this.externalSources = [];
			this.externalProfiles = [];
			this.phone = null;
			this.telegramChatId = null;
			this.telegramUsername = null;
			this.telegramUrl = null;
			this.avatarUrl = null;
			this.avatarSource = 'telegram';
			this.active = true;
			this.gmail = null;
			this.selectedDepartments = [];
			this.cabinetAccessEnabled = false;
			this.login = null;
			this.originalLogin = null;
			this.password = null;
			this.passwordTouched = false;
			this.showPassword = false;
			this.hasPassword = false;
			this.loginAvailable = true;
			this.selectedRoles = [];
			this.preferredWorkspaceKey = null;
			this.preferredWorkspaceOriginalKey = null;
			this.workspacePickerOpen = false;
			this.workspaceSearch = '';
			this.workspaceChoiceTouched = false;
			this.navigationPreferenceLoading = false;
			this.navigationPreferenceSaving = false;
			this.navigationPreferenceError = null;
			this.navigationPreferenceSaved = false;
			this.history = [];
			this.historyAccessDenied = false;
			this.canViewHistoryKnown = false;
			this.canViewHistoryFlag = false;
			this.deletionImpact = null;
			this.deletionImpactError = null;
			this.editMode = true;
			this.loadedFields = {};
			this.nextLocalRowId = 1;
			this.accountEmployeeMode = this.$route.query.employee_id
				? 'existing'
				: 'existing';
			this.accountCandidates = [];
			this.accountCandidatesLoading = false;
			this.selectedEmployeeCandidate = null;
			this.confirmUnverifiedEmployee = false;
			this.accountEmployeeDuplicates = [];
			this.accountEmployeeDuplicateConfirmed = false;
			this.accountGooglePreviewLoading = false;
			this.accountGooglePreviewError = '';
			this.accountGoogleContactRows = [];
			this.selectedAccountGoogleContactKey = null;
			this.showAccountGoogleContactModal = false;
		},
		fillForm(user) {
			this.loadedFields = Object.keys(user || {}).reduce((acc, key) => {
				acc[key] = true;
				return acc;
			}, {});
			this.historyAccessDenied = false;
			this.canViewHistoryKnown = Object.prototype.hasOwnProperty.call(user || {}, 'can_view_history');
			this.canViewHistoryFlag = Boolean(user && user.can_view_history);
			this.firstName = user.first_name;
			this.employeeCardId = user.employee_id || null;
			this.isBot = Boolean(user.isBot);
			this.lastName = user.last_name;
			this.middleName = user.middle_name || null;
			this.position = user.position || null;
			this.birthDate = user.birth_date || null;
			this.employmentOrganizationId = user.employment_organization_id || null;
			this.employmentOrganization = user.employment_organization || null;
			this.notes = user.notes || null;
			this.officeRoom = user.office_room || null;
			this.contactLabels = Array.isArray(user.contact_labels) ? user.contact_labels.slice() : [];
			this.customFields = Array.isArray(user.custom_fields)
				? user.custom_fields.map(item => Object.assign({}, item, {local_id: this.nextLocalRowId++}))
				: [];
			this.contactMethods = Array.isArray(user.contact_methods)
				? user.contact_methods.map(item => this.prepareContactMethod(item))
				: [];
			this.contactAddresses = Array.isArray(user.contact_addresses)
				? user.contact_addresses.map(item => Object.assign({}, item, {local_id: this.nextLocalRowId++}))
				: [];
			this.externalSources = Array.isArray(user.external_sources) ? user.external_sources.slice() : [];
			this.externalProfiles = Array.isArray(user.external_profiles)
				? user.external_profiles.map(item => Object.assign({}, item, {
					fields: Array.isArray(item.fields) ? item.fields.map(field => Object.assign({}, field)) : []
				}))
				: [];
			this.phone = formatPhone(user.phone);
			this.telegramChatId = user.chat_id;
			this.telegramUsername = user.telegram_username || null;
			this.telegramUrl = user.telegram_url || null;
			this.avatarUrl = user.avatar_url || (user.has_avatar && user.user_id ? `/api/users/${user.user_id}/avatar?v=${Date.now()}` : null);
			this.avatarSource = user.avatar_source || 'telegram';
			this.active = user.active !== false;
			this.gmail = user.gmail;
			this.cabinetAccessEnabled = Boolean(user.cabinet_access_enabled);
			this.login = user.login;
			this.originalLogin = user.login;
			this.hasPassword = Boolean(user.has_password);
			this.password = null;
			this.passwordTouched = false;
			this.showPassword = false;
			this.loginAvailable = true;
			this.selectedRoles = Array.isArray(user.roles)
				? user.roles.map(role => role.id).filter(roleId => roleId !== null && typeof roleId !== 'undefined')
				: [];
			this.selectedDepartments = Array.isArray(user.departments)
				? user.departments.map(department => typeof department === 'object' ? department.id : department)
				: [];
		},
		buildPayload() {
			const normalizedPhone = normalizePhoneForStorage(this.phone);
			const normalizedGmail = normalizeGmailForStorage(this.gmail);
			const data = {
				first_name: this.firstName,
				last_name: this.lastName,
				middle_name: this.middleName || null,
				position: this.position || null,
				birth_date: this.birthDate || null,
				employment_organization_id: this.employmentOrganizationId || null,
				notes: this.notes || null,
				office_room: this.officeRoom || null,
				contact_labels: this.contactLabels.slice(),
				custom_fields: this.customFields.map(field => ({
					label: field.label || null,
					value: field.value || null
				})),
				contact_methods: this.contactMethods.map((method, index) => ({
					contact_type: method.contact_type,
					label: method.label || null,
					value: this.normalizedContactMethodValue(method),
					sort_order: index,
					source: method.source || 'manual'
				})),
				contact_addresses: this.contactAddresses.map((address, index) => ({
					label: address.label || null,
					formatted: address.formatted || null,
					street: address.street || null,
					city: address.city || null,
					po_box: address.po_box || null,
					region: address.region || null,
					postal_code: address.postal_code || null,
					country: address.country || null,
					extended_address: address.extended_address || null,
					sort_order: index,
					source: address.source || 'manual'
				})),
				phone: normalizedPhone,
				chat_id: this.telegramChatId ? String(this.telegramChatId).trim() : null,
				isBot: this.isBot,
				gmail: normalizedGmail,
				avatar_source: this.avatarSource || 'telegram',
				departments: this.selectedDepartments.map(id => parseInt(id)).filter(id => !isNaN(id))
			};
			if (this.isEdit) {
				data.userId = this.userId;
			}
			if (
				this.isCreate
				&& this.accountEmployeeMode === 'existing'
				&& this.selectedEmployeeCandidate
			) {
				data.employee_id = Number(this.selectedEmployeeCandidate.employee_id);
				if (this.selectedEmployeeNeedsConfirmation) {
					data.confirm_unverified_employee =
						this.confirmUnverifiedEmployee === true;
				}
			} else if (
				this.isCreate
				&& this.accountEmployeeMode === 'new'
			) {
				data.create_new_employee = true;
				data.confirm_duplicate = this.accountEmployeeDuplicateConfirmed;
			}
			if (this.canManageAccess) {
				data.cabinet_access_enabled = Boolean(this.active && this.cabinetAccessEnabled);
				data.login = this.login;
				data.roles = this.selectedRoles;
				if (this.passwordTouched && this.password) {
					data.password = this.password;
				}
			}
			return data;
		},
		async loadAccountCandidates() {
			if (!this.isCreate) return;
			this.accountCandidatesLoading = true;
			try {
				const response = await api.get('/api/employees/account-candidates', {
					ignoreModal: true
				});
				const data = response && response.data || {};
				this.accountCandidates = Array.isArray(data.employees)
					? data.employees
					: [];
				const requestedEmployeeId = Number(this.$route.query.employee_id);
				if (Number.isInteger(requestedEmployeeId) && requestedEmployeeId > 0) {
					const selected = this.accountCandidateOptions.find(employee => (
						Number(employee.employee_id) === requestedEmployeeId
					));
					if (selected) {
						this.selectedEmployeeCandidate = selected;
						this.onEmployeeCandidateSelected(selected);
					}
				}
			} catch (error) {
				this.errorMessage = this.errorText(
					error,
					'Не удалось загрузить сотрудников без учётной записи.'
				);
			} finally {
				this.accountCandidatesLoading = false;
			}
		},
		onEmployeeCandidateSelected(employee) {
			this.confirmUnverifiedEmployee = false;
			if (!employee) {
				this.clearEmployeePrefill();
				return;
			}
			this.lastName = employee.last_name || null;
			this.firstName = employee.first_name || null;
			this.middleName = employee.middle_name || null;
			this.birthDate = employee.birth_date || null;
			this.position = employee.position || null;
			this.selectedDepartments = employee.operational_department_id
				? [Number(employee.operational_department_id)]
				: [];
			const methods = Array.isArray(employee.contact_methods)
				? employee.contact_methods
				: [];
			const primaryPhone = methods.find(method => (
				method.contact_type === 'phone' && method.is_primary
			)) || methods.find(method => method.contact_type === 'phone');
			const systemGmail = methods.find(method => (
				method.contact_type === 'email'
				&& /@g(?:oogle)?mail\.com$/i.test(method.value || '')
			));
			this.phone = primaryPhone ? formatPhone(primaryPhone.value) : null;
			this.gmail = systemGmail ? systemGmail.value : null;
			this.contactMethods = methods.filter(method => (
				method !== primaryPhone && method !== systemGmail
			)).map(method => this.prepareContactMethod(method));
		},
		accountEmployeeStatusLabel(status) {
			return {
				active: 'Действующий',
				reserve: 'Резерв',
				unverified: 'Непроверенный'
			}[status] || status || 'Статус не указан';
		},
		accountCandidateFilter(option, label, search) {
			const normalizedSearch = String(search || '').toLocaleLowerCase('ru');
			return String(
				option && (option.search_text || option.label) || label || ''
			).toLocaleLowerCase('ru').includes(normalizedSearch);
		},
		clearEmployeePrefill() {
			this.lastName = null;
			this.firstName = null;
			this.middleName = null;
			this.birthDate = null;
			this.position = null;
			this.phone = null;
			this.gmail = null;
			this.contactMethods = [];
			this.selectedDepartments = [];
		},
		async onAccountGoogleContactFile(event) {
			const file = event && event.target && event.target.files
				? event.target.files[0]
				: null;
			if (event && event.target) event.target.value = '';
			if (!file) return;
			this.accountGooglePreviewLoading = true;
			this.accountGooglePreviewError = '';
			this.accountGoogleContactRows = [];
			this.selectedAccountGoogleContactKey = null;
			try {
				const form = new FormData();
				form.append('file', file);
				const response = await api.post(
					'/api/employees/imports/google-contacts/preview',
					form,
					{
						headers: {'Content-Type': 'multipart/form-data'},
						ignoreModal: true
					}
				);
				this.accountGoogleContactRows = response
					&& response.data
					&& Array.isArray(response.data.rows)
					? response.data.rows
					: [];
				if (this.accountGoogleContactRows.length === 1) {
					this.selectedAccountGoogleContactKey =
						this.accountGoogleContactRows[0].decision_key;
				}
				this.showAccountGoogleContactModal = true;
			} catch (error) {
				this.errorMessage = this.errorText(
					error,
					'Не удалось прочитать Google CSV.'
				);
			} finally {
				this.accountGooglePreviewLoading = false;
			}
		},
		applyAccountGoogleContact() {
			const row = this.selectedAccountGoogleContact;
			if (!row) return;
			const matchedId = row.matched_employee
				? Number(row.matched_employee.employee_id)
				: null;
			const existing = matchedId
				? this.accountCandidateOptions.find(employee => (
					Number(employee.employee_id) === matchedId
				))
				: null;
			if (existing) {
				this.accountEmployeeMode = 'existing';
				this.selectedEmployeeCandidate = existing;
				this.onEmployeeCandidateSelected(existing);
				return;
			}
			if (matchedId) {
				this.accountGooglePreviewError =
					'Этот контакт уже связан с карточкой, которую нельзя использовать для новой учётной записи. Проверьте статус сотрудника и существующую связь.';
				this.showAccountGoogleContactModal = true;
				return;
			}
			this.accountEmployeeMode = 'new';
			const canonical = row.canonical || {};
			this.lastName = canonical.last_name || null;
			this.firstName = canonical.first_name || null;
			this.middleName = canonical.middle_name || null;
			this.birthDate = canonical.birth_date || null;
			this.position = canonical.position || null;
			const methods = Array.isArray(canonical.contact_methods)
				? canonical.contact_methods
				: [];
			const primaryPhone = methods.find(method => method.contact_type === 'phone');
			const systemGmail = methods.find(method => (
				method.contact_type === 'email'
				&& /@g(?:oogle)?mail\.com$/i.test(method.value || '')
			));
			this.phone = primaryPhone ? formatPhone(primaryPhone.value) : null;
			this.gmail = systemGmail ? systemGmail.value : null;
			this.contactMethods = methods.filter(method => (
				method !== primaryPhone && method !== systemGmail
			)).map(method => this.prepareContactMethod(method));
			this.contactAddresses = Array.isArray(canonical.contact_addresses)
				? canonical.contact_addresses.map(address => Object.assign({}, address, {
					local_id: this.nextLocalRowId++
				}))
				: [];
			this.notes = canonical.notes || null;
			this.contactLabels = Array.isArray(canonical.contact_labels)
				? canonical.contact_labels.slice()
				: [];
			this.customFields = Array.isArray(canonical.custom_fields)
				? canonical.custom_fields.map(field => Object.assign({}, field, {
					local_id: this.nextLocalRowId++
				}))
				: [];
		},
		accountGoogleContactSummary(row) {
			const methods = row && row.canonical
				&& Array.isArray(row.canonical.contact_methods)
				? row.canonical.contact_methods
				: [];
			return methods.map(method => method.value).filter(Boolean).join(' · ')
				|| 'Контактные данные не указаны';
		},
		buildProfilePayload() {
			const data = {};
			if (this.canEditSelfField('position')) {
				data.position = this.position || null;
			}
			if (this.canEditSelfField('gmail')) {
				data.gmail = normalizeGmailForStorage(this.gmail);
			}
			if (this.canEditSelfField('phone')) {
				data.phone = normalizePhoneForStorage(this.phone);
			}
			if (this.canEditSelfField('avatar_source')) {
				data.avatar_source = this.avatarSource || 'telegram';
			}
			if (this.canEditSelfField('details')) {
				data.middle_name = this.middleName || null;
				data.birth_date = this.birthDate || null;
				data.employment_organization_id = this.employmentOrganizationId || null;
				data.notes = this.notes || null;
				data.office_room = this.officeRoom || null;
				data.contact_labels = this.contactLabels.slice();
				data.custom_fields = this.customFields.map(field => ({
					label: field.label || null,
					value: field.value || null
				}));
				data.contact_addresses = this.contactAddresses.map((address, index) => Object.assign({}, address, {
					sort_order: index
				}));
			}
			if (this.canEditAnySelfContact) {
				data.contact_methods = this.contactMethods.map((method, index) => ({
					contact_type: method.contact_type,
					label: method.label || null,
					value: this.normalizedContactMethodValue(method),
					sort_order: index,
					source: method.source || 'manual'
				}));
			}
			return data;
		},
		async submit() {
			const shouldValidateGmail = !this.isProfile || this.canEditSelfField('gmail');
			const shouldValidatePhone = !this.isProfile || this.canEditSelfField('phone');
			if (shouldValidatePhone && this.phoneValidationError) {
				this.errorMessage = this.phoneValidationError;
				return;
			}
			if (this.contactMethodsPhoneValidationError) {
				this.errorMessage = this.contactMethodsPhoneValidationError;
				return;
			}
			if (shouldValidateGmail && this.gmailValidationError) {
				this.errorMessage = this.gmailValidationError;
				return;
			}
			this.saving = true;
			this.errorMessage = null;
			try {
				if (
					this.isCreate
					&& this.accountEmployeeMode === 'new'
					&& !this.accountEmployeeDuplicateConfirmed
				) {
					const duplicateResponse = await api.get(
						'/api/employees/duplicate-candidates',
						{
							params: {
								last_name: this.lastName,
								first_name: this.firstName,
								birth_date: this.birthDate || undefined,
								contact_methods: this.contactMethods.length
									? JSON.stringify(this.buildPayload().contact_methods)
									: undefined
							},
							ignoreModal: true
						}
					);
					this.accountEmployeeDuplicates = duplicateResponse
						&& duplicateResponse.data
						&& Array.isArray(duplicateResponse.data.employees)
						? duplicateResponse.data.employees
						: [];
					if (this.accountEmployeeDuplicates.length) return;
				}
				if (this.isProfile) {
					const profile = await this.$store.dispatch('updateMyProfile', this.buildProfilePayload());
					this.directorySettings = profile.settings || this.directorySettings;
					this.profileUserId = profile.user && profile.user.user_id;
					this.fillForm(profile.user || {});
					this.editMode = false;
					await this.loadHistoryIfVisible();
					return;
				}
				const action = this.isEdit ? 'editUser' : 'createUser';
				const result = await this.$store.dispatch(action, this.buildPayload());
				await this.$store.dispatch('getUsers', {full: true}).catch(() => {});
				if (!this.isEdit && result && result.user_id) {
					await this.$router.replace({name: 'user-card', params: {id: result.user_id}});
					return;
				}
				if (this.isEdit) {
					const user = await this.$store.dispatch('getUser', this.userId);
					this.fillForm(user);
					this.editMode = false;
					await this.loadHistoryIfVisible();
				}
			} catch (e) {
				this.errorMessage = this.errorText(e);
			} finally {
				this.saving = false;
			}
		},
		toggleWorkspacePicker() {
			this.workspacePickerOpen = !this.workspacePickerOpen;
			if (this.workspacePickerOpen) this.$nextTick(() => this.$refs.workspaceSearch && this.$refs.workspaceSearch.focus());
		},
		onWorkspaceChoice() {
			this.workspaceChoiceTouched = true;
			this.navigationPreferenceSaved = false;
			this.navigationPreferenceError = null;
		},
		async loadNavigationPreferences() {
			if (!this.isProfile) {
				return;
			}
			this.navigationPreferenceLoading = true;
			this.workspaceChoiceTouched = false;
			this.navigationPreferenceError = null;
			this.navigationPreferenceSaved = false;
			try {
				const accessLoads = [
					this.$store.dispatch('objectCards/fetchAccessSettings').catch(() => {})
				];
				if (!this.$can('read', 'fuelLimits')) {
					accessLoads.push(this.$store.dispatch('getObjectsInProcess').catch(() => {}));
				}
				await Promise.all(accessLoads);
				const settings = await this.$store.dispatch('navigation_loadSettings');
				const configuredKey = settings && settings.preferred_workspace_key
					? settings.preferred_workspace_key
					: null;
				const availableKeys = getAvailableWorkspaces(this).map(workspace => workspace.key);
				const selectedKey = configuredKey && availableKeys.includes(configuredKey)
					? configuredKey
					: null;
				this.preferredWorkspaceKey = selectedKey;
				// Если сохранённый раздел больше недоступен, показываем fallback,
				// но сохраняем исходный ключ, чтобы кнопка могла явно сбросить его.
				this.preferredWorkspaceOriginalKey = configuredKey;
			} catch (e) {
				this.navigationPreferenceError = this.errorText(e, 'Не удалось загрузить настройку навигации.');
			} finally {
				this.navigationPreferenceLoading = false;
			}
		},
		async savePreferredWorkspace() {
			if (!this.isProfile || !this.preferredWorkspaceChanged || this.navigationPreferenceSaving) {
				return;
			}
			this.navigationPreferenceSaving = true;
			this.navigationPreferenceError = null;
			this.navigationPreferenceSaved = false;
			try {
				const settings = await this.$store.dispatch(
					'navigation_updatePreferredWorkspace',
					this.preferredWorkspaceKey || null
				);
				const savedKey = settings && settings.preferred_workspace_key
					? settings.preferred_workspace_key
					: null;
				this.preferredWorkspaceKey = savedKey;
				this.preferredWorkspaceOriginalKey = savedKey;
				this.workspaceChoiceTouched = false;
				this.navigationPreferenceSaved = true;
			} catch (e) {
				this.navigationPreferenceError = this.errorText(e, 'Не удалось сохранить настройку навигации.');
			} finally {
				this.navigationPreferenceSaving = false;
			}
		},
		normalizeGmailField() {
			try {
				this.gmail = normalizeGmailForStorage(this.gmail);
			} catch (e) {
				// Keep the entered value visible so the user can fix it.
			}
		},
		normalizedContactMethodValue(method) {
			if (!method || method.contact_type !== 'phone') {
				return method && method.value ? method.value : null;
			}
			return formatPhoneInput(method.value, 'contact') || null;
		},
		prepareContactMethod(method) {
			return Object.assign({}, method, {
				local_id: this.nextLocalRowId++,
				value: method && method.contact_type === 'phone'
					? formatPhoneInput(method.value, 'contact')
					: method && method.value
			});
		},
		async loadEmploymentOrganizations() {
			try {
				this.employmentOrganizations = await this.$store.dispatch(
					'getEmploymentOrganizations',
					this.canSave
				);
			} catch (e) {
				this.employmentOrganizations = [];
			}
		},
		addContactMethod() {
			this.contactMethods.push({
				local_id: this.nextLocalRowId++,
				contact_type: !this.isProfile || this.canEditSelfField('phone') ? 'phone' : 'email',
				label: '',
				value: '',
				source: 'manual'
			});
		},
		removeContactMethod(index) {
			this.contactMethods.splice(index, 1);
		},
		canEditSelfContactMethod(method) {
			if (!this.isProfile) {
				return true;
			}
			return method && method.contact_type === 'email'
				? this.canEditSelfField('gmail')
				: this.canEditSelfField('phone');
		},
		addContactAddress() {
			this.contactAddresses.push({
				local_id: this.nextLocalRowId++,
				label: '',
				formatted: '',
				street: '',
				city: '',
				po_box: '',
				region: '',
				postal_code: '',
				country: '',
				extended_address: '',
				source: 'manual'
			});
		},
		removeContactAddress(index) {
			this.contactAddresses.splice(index, 1);
		},
		addCustomField() {
			this.customFields.push({
				local_id: this.nextLocalRowId++,
				label: '',
				value: ''
			});
		},
		removeCustomField(index) {
			this.customFields.splice(index, 1);
		},
		addressDisplay(address) {
			if (!address) {
				return 'Адрес не указан';
			}
			if (address.formatted) {
				return address.formatted;
			}
			return [
				address.street,
				address.city,
				address.region,
				address.postal_code,
				address.country
			].filter(Boolean).join(', ') || 'Адрес не указан';
		},
		formatBirthDate(value) {
			if (!value) {
				return '';
			}
			const date = new Date(`${value}T00:00:00`);
			return Number.isNaN(date.getTime())
				? value
				: date.toLocaleDateString('ru-RU', {day: 'numeric', month: 'long', year: 'numeric'});
		},
		sourceLabel(source) {
			if (source === 'google_contacts') {
				return 'Google Контакты';
			}
			if (source === '1c') {
				return '1С';
			}
			return source;
		},
		openOrganizationModal() {
			this.organizationError = null;
			this.organizationForm = {
				name: '',
				short_name: '',
				aliases: ''
			};
			this.showOrganizationModal = true;
		},
		async createOrganization() {
			if (!this.organizationForm.name || this.organizationSaving) {
				return;
			}
			this.organizationSaving = true;
			this.organizationError = null;
			try {
				const organization = await this.$store.dispatch('createEmploymentOrganization', {
					name: this.organizationForm.name,
					short_name: this.organizationForm.short_name || null,
					aliases: String(this.organizationForm.aliases || '')
						.split(',')
						.map(item => item.trim())
						.filter(Boolean)
				});
				await this.loadEmploymentOrganizations();
				this.employmentOrganizationId = organization.id;
				this.employmentOrganization = organization;
				this.showOrganizationModal = false;
			} catch (e) {
				this.organizationError = this.errorText(e, 'Не удалось создать организацию.');
			} finally {
				this.organizationSaving = false;
			}
		},
		async exportGoogleContact() {
			if (!this.userId || this.exporting) {
				return;
			}
			this.exporting = true;
			this.errorMessage = null;
			try {
				const response = await this.$store.dispatch('exportEmployeeGoogleContact', this.userId);
				this.downloadBlob(
					response.data,
					`${this.fullName || 'Сотрудник'} — Google Контакты.csv`
				);
			} catch (e) {
				this.errorMessage = this.errorText(e, 'Не удалось экспортировать контакт.');
			} finally {
				this.exporting = false;
			}
		},
		downloadBlob(blob, fileName) {
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		},
		async loadDirectorySettings() {
			this.directorySettings = await this.$store.dispatch('getEmployeeDirectorySettings')
				.catch(() => this.directorySettings);
		},
		async reloadCurrentForm() {
			if (this.isProfile) {
				const profile = await this.$store.dispatch('getMyProfile');
				this.directorySettings = profile.settings || this.directorySettings;
				this.profileUserId = profile.user && profile.user.user_id;
				this.fillForm(profile.user || {});
				return;
			}
			if (this.userId) {
				const user = await this.loadUserForCard();
				this.fillForm(user);
			}
		},
		async loadUserForCard() {
			if (this.canReadRawUsers) {
				try {
					return await this.$store.dispatch('getUser', this.userId);
				} catch (e) {
					return await this.$store.dispatch('getEmployeeCard', this.userId);
				}
			}
			return await this.$store.dispatch('getEmployeeCard', this.userId);
		},
		async loadHistoryIfVisible() {
			if (!this.canViewHistory) {
				this.history = [];
				return;
			}
			await this.loadHistory();
		},
		startEdit() {
			this.editMode = true;
		},
		async cancelEdit() {
			if (this.isCreate) {
				this.goBack();
				return;
			}
			this.errorMessage = null;
			await this.reloadCurrentForm();
			this.editMode = false;
		},
		canEditSelfField(field) {
			return Boolean(this.selfEditConfig && this.selfEditConfig[field]);
		},
		hasEmployeeField(field) {
			return Boolean(this.loadedFields && this.loadedFields[field]);
		},
		chooseAvatarFile() {
			if (!this.canEditAvatar || !this.$refs.avatarInput) {
				return;
			}
			this.$refs.avatarInput.click();
		},
		async onAvatarFileSelected(event) {
			const file = event && event.target && event.target.files ? event.target.files[0] : null;
			if (event && event.target) {
				event.target.value = '';
			}
			if (!file || !this.userId) {
				return;
			}
			this.saving = true;
			this.errorMessage = null;
			try {
				await this.$store.dispatch('uploadUserAvatar', {
					userId: this.userId,
					file
				});
				await this.reloadCurrentForm();
				await this.loadHistoryIfVisible();
			} catch (e) {
				this.errorMessage = this.errorText(e);
			} finally {
				this.saving = false;
			}
		},
		async deleteAvatar() {
			if (!this.canEditAvatar || !this.userId) {
				return;
			}
			this.saving = true;
			this.errorMessage = null;
			try {
				await this.$store.dispatch('deleteUserAvatar', this.userId);
				await this.reloadCurrentForm();
				await this.loadHistoryIfVisible();
			} catch (e) {
				this.errorMessage = this.errorText(e);
			} finally {
				this.saving = false;
			}
		},
		async openStatusModal() {
			if (!this.canManageAccess || this.isCurrentUser || this.saving) {
				return;
			}
			this.showStatusModal = true;
			this.statusImpactLoading = true;
			this.statusImpact = null;
			this.statusError = null;
			try {
				this.statusImpact = await this.$store.dispatch('getUserStatusImpact', this.userId);
			} catch (e) {
				this.statusError = this.errorText(e, 'Не удалось проверить последствия изменения статуса.');
			} finally {
				this.statusImpactLoading = false;
			}
		},
		async applyStatusChange() {
			if (!this.statusImpact || this.statusSubmitting || this.saving) {
				return;
			}
			const targetActive = this.statusTargetActive;
			const keepEditing = this.isEditingForm;
			this.statusSubmitting = true;
			this.statusError = null;
			try {
				const result = await this.$store.dispatch('changeUserActive', {
					id: this.userId,
					active: targetActive
				});
				if (keepEditing) {
					this.active = result && result.active !== false;
					this.cabinetAccessEnabled = Boolean(result && result.cabinet_access_enabled);
				} else {
					this.fillForm(result || {});
					this.editMode = false;
				}
				await this.$store.dispatch('getUsers', {full: true}).catch(() => {});
				await this.loadHistoryIfVisible();
				this.showStatusModal = false;

				let message = 'Учётная запись отключена. Личный кабинет и активные сессии отключены.';
				let variant = 'warning';
				if (targetActive) {
					variant = result && result.cabinet_access_restore_failed ? 'warning' : 'success';
					message = result && result.cabinet_access_restored
						? 'Учётная запись включена. Доступ в личный кабинет восстановлен.'
						: result && result.cabinet_access_restore_failed
							? 'Учётная запись включена, но личный кабинет остался выключен: нет логина или пароля.'
							: 'Учётная запись включена. Личный кабинет остался выключен.';
				}
				this.$bvToast.toast(message, {
					title: targetActive ? 'Учётная запись включена' : 'Учётная запись отключена',
					variant,
					solid: true
				});
				if (!targetActive && this.canShowDangerZone) {
					await this.loadDeletionImpact();
				} else {
					this.deletionImpact = null;
					this.deletionImpactError = null;
				}
			} catch (e) {
				this.statusError = this.errorText(e, 'Не удалось изменить статус учётной записи.');
			} finally {
				this.statusSubmitting = false;
			}
		},
		resetStatusModal() {
			if (this.statusSubmitting) {
				return;
			}
			this.statusImpact = null;
			this.statusError = null;
			this.statusImpactLoading = false;
		},
		async loadDeletionImpact() {
			if (!this.canShowDangerZone || this.deletionImpactLoading) {
				return;
			}
			this.deletionImpactLoading = true;
			this.deletionImpact = null;
			this.deletionImpactError = null;
			try {
				this.deletionImpact = await this.$store.dispatch('getUserDeletionImpact', this.userId);
			} catch (e) {
				this.deletionImpactError = this.errorText(e, 'Не удалось проверить связи сотрудника.');
			} finally {
				this.deletionImpactLoading = false;
			}
		},
		openPermanentDeleteModal() {
			if (!this.canDeletePermanently) {
				return;
			}
			this.deletionAcknowledged = false;
			this.permanentDeleteError = null;
			this.showPermanentDeleteModal = true;
		},
		resetPermanentDeleteModal() {
			if (this.deletionSubmitting) {
				return;
			}
			this.deletionAcknowledged = false;
			this.permanentDeleteError = null;
		},
		async deletePermanently() {
			if (!this.canDeletePermanently || !this.deletionAcknowledged || this.deletionSubmitting) {
				return;
			}
			this.deletionSubmitting = true;
			this.permanentDeleteError = null;
			try {
				await this.$store.dispatch('deleteUser', {
					userId: this.userId,
					acknowledgedIrreversible: true
				});
				await this.$store.dispatch('getUsers', {full: true}).catch(() => {});
				this.showPermanentDeleteModal = false;
				await this.$router.push({name: 'users'});
			} catch (e) {
				const impact = e && e.response && e.response.data && e.response.data.impact;
				if (impact) {
					this.deletionImpact = impact;
				}
				this.permanentDeleteError = this.errorText(e, 'Не удалось удалить сотрудника.');
			} finally {
				this.deletionSubmitting = false;
			}
		},
		async loadHistory() {
			if (!this.isEdit || !this.canViewHistory) {
				return;
			}
			this.historyLoading = true;
			try {
				this.history = await this.$store.dispatch('getUserHistory', this.userId);
			} catch (e) {
				if (e && e.response && e.response.status === 403) {
					this.historyAccessDenied = true;
				}
				this.history = [];
			} finally {
				this.historyLoading = false;
			}
		},
		onLoginFieldChange() {
			if (!this.canManageAccess) {
				return;
			}
			if (!this.login) {
				this.loginAvailable = false;
				return;
			}
			if (this.login === this.originalLogin) {
				this.loginAvailable = true;
				return;
			}
			this.$store.dispatch('checkLoginAvailable', this.login)
				.then(({available}) => this.loginAvailable = available)
				.catch(() => this.loginAvailable = false);
		},
		onPasswordInput() {
			this.passwordTouched = true;
		},
		getRandomInt(max) {
			if (window.crypto && window.crypto.getRandomValues) {
				const array = new Uint32Array(1);
				window.crypto.getRandomValues(array);
				return array[0] % max;
			}
			return Math.floor(Math.random() * max);
		},
		getRandomChar(chars) {
			return chars[this.getRandomInt(chars.length)];
		},
		generatePassword() {
			const groups = [
				'ABCDEFGHJKLMNPQRSTUVWXYZ',
				'abcdefghijkmnopqrstuvwxyz',
				'23456789',
				'!@#$%'
			];
			const allChars = groups.join('');
			let chars = groups.map(group => this.getRandomChar(group));
			while (chars.length < 7) {
				chars.push(this.getRandomChar(allChars));
			}
			for (let i = chars.length - 1; i > 0; i -= 1) {
				const j = this.getRandomInt(i + 1);
				const temp = chars[i];
				chars[i] = chars[j];
				chars[j] = temp;
			}
			this.password = chars.join('');
			this.passwordTouched = true;
			this.showPassword = true;
		},
		togglePasswordVisibility() {
			this.showPassword = !this.showPassword;
		},
		historyChanges(item) {
			if (!item || !item.changes) {
				return [];
			}
			if (Array.isArray(item.changes)) {
				return item.changes;
			}
			try {
				const parsed = JSON.parse(item.changes);
				return Array.isArray(parsed) ? parsed : [];
			} catch (e) {
				return [];
			}
		},
		historyActor(item) {
			const actor = item && item.Actor;
			if (!actor) {
				return 'Система';
			}
			return [actor.last_name, actor.first_name].filter(Boolean).join(' ') || actor.login || `#${actor.user_id}`;
		},
		historyActionLabel(action) {
			const labels = {
				created: 'Создание',
				updated: 'Изменение',
				deleted: 'Удаление',
				activated: 'Сделан действующим',
				deactivated: 'Отключён',
				profile_updated: 'Профиль',
				import_created: 'Импорт',
				import_updated: 'Импорт',
				avatar_updated: 'Аватарка',
				avatar_deleted: 'Аватарка',
				telegram_avatar_synced: 'Аватарка'
			};
			return labels[action] || action;
		},
		historyVariant(action) {
			const variants = {
				created: 'success',
				updated: 'primary',
				deleted: 'danger',
				activated: 'success',
				deactivated: 'secondary',
				profile_updated: 'primary',
				import_created: 'success',
				import_updated: 'info',
				avatar_updated: 'info',
				avatar_deleted: 'secondary',
				telegram_avatar_synced: 'info'
			};
			return variants[action] || 'secondary';
		},
		formatDate(dateString) {
			if (!dateString) {
				return '';
			}
			const date = new Date(dateString);
			const datePart = date.toLocaleDateString('ru-RU', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric'
			});
			const timePart = date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit'
			});
			return `${datePart}, ${timePart}`;
		},
		errorText(e, fallback = 'Не удалось сохранить сотрудника.') {
			const data = e && e.response && e.response.data;
			if (data && data.msg) {
				return data.msg;
			}
			if (typeof data === 'string' && data.trim()) {
				return data;
			}
			return e && e.message ? e.message : fallback;
		},
		goBack() {
			const query = !this.isProfile && !this.isCreate && this.userId
				? {highlight: String(this.userId)}
				: {};
			this.$router.push({name: 'users', query}).catch(() => {});
		}
	}
};
</script>

<style scoped>
.user-card-page {
	min-width: 0;
}

.user-card-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 12px;
}

.user-card-header__actions {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 6px;
}

.user-card-layout {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
	gap: 12px;
	align-items: start;
}

.user-card-main {
	display: grid;
	gap: 12px;
}

.user-avatar-section {
	display: grid;
	grid-template-columns: 112px minmax(0, 1fr);
	gap: 14px;
	align-items: center;
}

.user-avatar-preview {
	width: 112px;
	height: 112px;
	border-radius: 8px;
	overflow: hidden;
	background: #eef2f5;
}

.user-avatar-preview__img,
.user-avatar-preview__fallback {
	width: 100%;
	height: 100%;
}

.user-avatar-preview__img {
	display: block;
	object-fit: cover;
}

.user-avatar-preview__fallback {
	display: flex;
	align-items: center;
	justify-content: center;
	color: #495057;
	font-size: 32px;
	font-weight: 700;
}

.user-avatar-controls {
	min-width: 0;
}

.user-avatar-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.user-profile-overview {
	display: grid;
	grid-template-columns: 112px minmax(0, 1fr);
	gap: 14px;
	align-items: start;
}

.user-profile-overview__avatar {
	align-self: start;
}

.user-profile-overview__body {
	min-width: 0;
}

.user-profile-overview__subtitle {
	margin-bottom: 10px;
	color: #6c757d;
	font-size: 14px;
}

.user-profile-facts {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 8px;
}

.user-profile-fact {
	min-width: 0;
	padding: 8px;
	border: 1px solid #edf0f2;
	border-radius: 6px;
	background: #f8f9fa;
}

.user-profile-fact--wide {
	grid-column: 1 / -1;
}

.user-profile-fact span {
	display: block;
	margin-bottom: 2px;
	color: #6c757d;
	font-size: 12px;
}

.user-profile-fact strong {
	display: block;
	min-width: 0;
	overflow-wrap: anywhere;
	color: #212529;
	font-size: 14px;
}

.user-profile-link {
	display: inline-block;
	min-width: 0;
	overflow-wrap: anywhere;
	font-size: 14px;
	font-weight: 700;
}

.user-card-section {
	padding: 12px;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	background: #fff;
}

.profile-navigation-preference {
	border-color: #d8e4ee;
	background: #f9fcff;
	box-shadow: inset 0 1px 0 #fff, 0 8px 24px rgba(38, 69, 92, .06);
}

.profile-navigation-preference__controls {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	gap: 10px;
	align-items: stretch;
}

.profile-navigation-preference__current {
	display: flex;
	align-items: center;
	gap: 12px;
	min-width: 0;
	padding: 9px 10px;
	border: 1px solid #dce6ef;
	border-radius: 10px;
	background: #fff;
}

.profile-navigation-preference__current-icon,
.profile-navigation-preference__choice-icon {
	flex: 0 0 38px;
	width: 38px;
	height: 38px;
	display: grid;
	place-items: center;
	border-radius: 10px;
	background: #edf5fa;
	color: #327499;
}

.profile-navigation-preference__current-copy,
.profile-navigation-preference__choice-label {
	display: flex;
	flex-direction: column;
	min-width: 0;
	line-height: 1.3;
}

.profile-navigation-preference__current-copy { flex: 1; }
.profile-navigation-preference__current-copy small,
.profile-navigation-preference__choice-label small { color: #667b8c; font-size: 12px; }
.profile-navigation-preference__current-copy strong,
.profile-navigation-preference__choice-label strong { color: #243b4a; font-size: 14px; overflow-wrap: anywhere; }

.profile-navigation-preference__choose {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	min-height: 38px;
	padding: 6px 11px;
	border: 1px solid #bad5e7;
	border-radius: 8px;
	background: #f3f9fd;
	color: #1c587e;
	font-weight: 700;
}
.profile-navigation-preference__choose:hover { background: #e8f4fb; }
.profile-navigation-preference__choose:focus-visible { outline: 2px solid #247eb3; outline-offset: 2px; }
.profile-navigation-preference__choose:disabled { opacity: .6; cursor: wait; }

.profile-navigation-preference__feedback {
	margin-top: 9px;
	padding: 8px 10px;
	border-radius: 8px;
	font-size: 13px;
}
.profile-navigation-preference__feedback--error { background: #fff3f2; color: #923527; border: 1px solid #f0c6c0; }
.profile-navigation-preference__feedback--saved { background: #edf9f2; color: #206341; border: 1px solid #bfe3cd; }

.profile-navigation-preference__picker {
	margin-top: 10px;
	padding: 12px;
	border: 1px solid #dce8f0;
	border-radius: 10px;
	background: #fff;
}
.profile-navigation-preference__search-label { display: block; margin-bottom: 5px; color: #475f70; font-size: 12px; font-weight: 700; }
.profile-navigation-preference__search { max-width: 460px; background: #fff; }
.profile-navigation-preference__choices { max-height: 370px; overflow-y: auto; margin-top: 10px; padding-right: 3px; }
.profile-navigation-preference__group { margin-top: 12px; }
.profile-navigation-preference__group h6 { margin: 0 0 7px; color: #647c8e; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; }
.profile-navigation-preference__group-items { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; }
.profile-navigation-preference__choice {
	position: relative;
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
	min-height: 58px;
	margin: 0;
	padding: 7px 10px;
	border: 1px solid #e0e8ee;
	border-radius: 9px;
	background: #fff;
	cursor: pointer;
}
.profile-navigation-preference__choice:hover { background: #f6fafc; border-color: #b9d4e4; }
.profile-navigation-preference__choice.is-selected { background: #eef9f6; border-color: #7ac3ac; box-shadow: inset 0 0 0 1px #b5dfd0; }
.profile-navigation-preference__choice input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.profile-navigation-preference__choice:focus-within { outline: 2px solid #247eb3; outline-offset: 2px; }
.profile-navigation-preference__choice-icon { flex-basis: 34px; width: 34px; height: 34px; border-radius: 8px; font-size: 14px; }
.profile-navigation-preference__check { margin-left: auto; color: #258969; opacity: 0; }
.profile-navigation-preference__choice.is-selected .profile-navigation-preference__check { opacity: 1; }
.profile-navigation-preference__empty { margin: 14px 0 4px; color: #607587; font-size: 13px; }
@media (max-width: 575.98px) {
	.profile-navigation-preference__group-items { grid-template-columns: minmax(0, 1fr); }
	.profile-navigation-preference__current { flex-wrap: wrap; }
	.profile-navigation-preference__choose { margin-left: auto; }
}

.user-danger-zone {
	border-color: #dc3545;
	background: #fffafa;
}

.user-impact-list {
	display: grid;
	gap: 6px;
	padding-left: 0;
	list-style: none;
}

.user-impact-list li {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	padding: 6px 8px;
	border: 1px solid #e9ecef;
	border-radius: 5px;
	background: #fff;
}

.user-card-section__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
	margin-bottom: 10px;
}

.user-card-section__header h6 {
	margin: 0;
	font-weight: 700;
}

.user-card-section__header--sub {
	margin-top: 14px;
	padding-top: 10px;
	border-top: 1px solid #edf0f2;
}

.employee-contact-list {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 8px 16px;
}

.employee-contact-row {
	display: grid;
	grid-template-columns: 24px minmax(0, 1fr);
	gap: 8px;
	align-items: start;
	min-width: 0;
	padding: 6px 0;
}

.employee-contact-row__icon {
	margin-top: 4px;
	color: #4f7cac;
	text-align: center;
}

.employee-contact-row a,
.employee-contact-row div {
	overflow-wrap: anywhere;
}

.employee-details-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 10px;
}

.employee-detail {
	min-width: 0;
}

.employee-detail--wide {
	grid-column: 1 / -1;
}

.employee-detail > span {
	display: block;
	margin-bottom: 2px;
	color: #6c757d;
	font-size: 12px;
}

.employee-detail__multiline {
	white-space: pre-wrap;
	overflow-wrap: anywhere;
}

.employee-external-profile {
	padding-top: 8px;
	border-top: 1px solid #edf0f2;
}

.employee-external-profile__fields {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 8px;
}

.employee-external-profile__fields > div {
	min-width: 0;
	padding: 7px;
	border-radius: 5px;
	background: #f8f9fa;
}

.employee-external-profile__fields small,
.employee-external-profile__fields strong {
	display: block;
	overflow-wrap: anywhere;
}

.employee-external-profile__fields small {
	color: #6c757d;
}

.employee-repeatable-list,
.employee-address-list {
	display: grid;
	gap: 8px;
}

.employee-repeatable-row {
	display: grid;
	gap: 8px;
	align-items: start;
}

.employee-repeatable-row--contact {
	grid-template-columns: minmax(110px, .55fr) minmax(150px, .8fr) minmax(220px, 1.5fr) auto;
}

.employee-repeatable-row--custom {
	grid-template-columns: minmax(160px, .8fr) minmax(220px, 1.4fr) auto;
}

.employee-address-card {
	padding: 10px;
	border: 1px solid #e7eaed;
	border-radius: 6px;
	background: #f8f9fa;
}

.employee-address-card__head {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	gap: 8px;
	margin-bottom: 8px;
}

.employee-address-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 8px;
}

.user-card-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 10px;
}

.user-card-switches {
	display: flex;
	flex-wrap: wrap;
	gap: 18px;
	margin-bottom: 12px;
}

.user-card-cabinet {
	margin-top: 12px;
}

.account-employee-link {
	padding: 12px;
	margin-bottom: 16px;
	border: 1px solid #cbdbea;
	border-radius: 9px;
	background: #f5f9fd;
}

.account-google-contact-list {
	display: grid;
	gap: 8px;
	max-height: 55vh;
	overflow: auto;
}

.account-google-contact {
	display: grid;
	grid-template-columns: auto minmax(0, 1fr);
	gap: 10px;
	align-items: start;
	padding: 10px;
	margin: 0;
	border: 1px solid #dce4ec;
	border-radius: 8px;
	cursor: pointer;
}

.account-google-contact--selected {
	border-color: #2d72d9;
	background: #edf5ff;
	box-shadow: 0 0 0 1px rgba(45, 114, 217, 0.12);
}

.account-google-contact strong,
.account-google-contact small {
	display: block;
}

.account-google-contact small {
	margin-top: 3px;
	color: #68798b;
}

.user-card-history {
	position: sticky;
	top: 8px;
}

.user-card-history__panel {
	max-height: calc(100vh - 110px);
	overflow: auto;
}

.user-history-list {
	display: grid;
	gap: 8px;
}

.user-history-item {
	padding: 8px;
	border: 1px solid #edf0f2;
	border-radius: 6px;
	background: #f8f9fa;
}

.user-history-item__head {
	display: flex;
	justify-content: space-between;
	gap: 8px;
}

.user-history-item__actor {
	margin-top: 3px;
	color: #495057;
	font-size: 13px;
	font-weight: 700;
}

.user-history-item__description {
	margin-top: 6px;
	color: #6c757d;
	font-size: 13px;
}

.user-history-changes {
	display: grid;
	gap: 5px;
	margin-top: 8px;
}

.user-history-change {
	display: grid;
	grid-template-columns: 120px minmax(0, 1fr);
	gap: 8px;
	font-size: 12px;
}

.user-history-change__label {
	color: #495057;
	font-weight: 700;
}

.user-history-change__value {
	min-width: 0;
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
	align-items: start;
	gap: 4px;
	overflow-wrap: anywhere;
	color: #212529;
}

.user-history-change__value span {
	white-space: pre-line;
}

@media (max-width: 992px) {
	.user-card-layout {
		grid-template-columns: 1fr;
	}

	.user-card-history {
		position: static;
	}

	.user-card-history__panel {
		max-height: none;
	}
}

@media (max-width: 767.98px) {
	.user-card-main,
	.user-card-section,
	.user-profile-overview__body { min-width: 0; }
	.user-card-header h4 { font-size: 1.25rem; overflow-wrap: anywhere; }
	.user-card-header__actions .btn {
		min-height: 44px;
		white-space: normal;
		line-height: 1.3;
	}
	.user-card-page ::v-deep .form-control,
	.user-card-page ::v-deep .custom-select,
	.user-card-page ::v-deep .vs__search { font-size: 16px; }
	.user-card-page ::v-deep input.form-control,
	.user-card-page ::v-deep .custom-select { min-height: 44px; }
	.user-card-section__header { flex-wrap: wrap; gap: 8px; }
	.employee-contact-row > div,
	.user-profile-fact { min-width: 0; overflow-wrap: anywhere; }
	.profile-navigation-preference__controls {
		grid-template-columns: minmax(0, 1fr);
	}

	.user-card-header {
		align-items: stretch;
		flex-direction: column;
	}

	.user-card-header__actions {
		justify-content: stretch;
	}

	.user-card-header__actions .btn {
		flex: 1 1 auto;
	}

	.user-avatar-section {
		grid-template-columns: 1fr;
	}

	.user-profile-overview,
	.user-profile-facts {
		grid-template-columns: 1fr;
	}

	.user-card-grid,
	.user-history-change,
	.employee-contact-list,
	.employee-details-grid,
	.employee-external-profile__fields,
	.employee-repeatable-row--contact,
	.employee-repeatable-row--custom,
	.employee-address-grid {
		grid-template-columns: 1fr;
	}

	.employee-repeatable-row .btn {
		justify-self: end;
	}
}
</style>
